import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import signalRService from '../services/signalRService';
import glossaryService from '../services/glossaryService';
import { useAuth } from '../contexts/AuthContext';
import { getJwtToken } from '../utils/jwtService';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { retryStopSession } from '../utils/retryStopSession';
import { getGlossaryDisplayName } from '../utils/glossaryUtils';
import { renderWithGlossaryHighlights } from '../utils/highlightGlossary';
import { startSessionTrace, trackEvent, trackException, clearTraceContext, getCurrentTraceId } from '../services/appInsightsService';
import { buildSonioxQueryParams } from './sonioxQueryParams';
import { resolveFunctionsTarget } from '../utils/environmentConfig';

import {
    Mic, Settings, X,
    Play, Square, Copy, Pause, Check,
    Lock, WifiOff, AlertCircle, Book,
    LogOut, Shield, Bug, Download as DownloadIcon, Bot
} from 'lucide-react';

import Button from './ui/Button';
import Input from './ui/Input';
import './TranslationHost-revised.css';
import LanguageSelector from './LanguageSelector';
import { canReadPlatformAdmin } from '../utils/platformAccess';

const TranslationHostRevised = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Form state
    const [sessionId, setSessionId] = useState('');
    const [sourceLanguage, setSourceLanguage] = useState('en-US');
    const [targetLanguages, setTargetLanguages] = useState(['ko-KR']);

    const [enableTts, setEnableTts] = useState(false);
    const [enablePartialText, setEnablePartialText] = useState(false);
    const [partialTextMode, setPartialTextMode] = useState('original'); // 'original' | 'translated'
    // PIM (Personal Interpreter Mode / iOS "Translate Around Me") simulation. When ON,
    // session-create POST body sends productLine="personal", mode="personal",
    // productType="" so the server tags the session as a PIM session — same path
    // the iOS QolSync AI client uses. Existing UI defaults (enableTts=false,
    // partialTextMode="original") already match PIM client behaviour, so no other
    // overrides are required.
    const [simulatePimMode, setSimulatePimMode] = useState(false);
    // SuperAdmin-only plan simulation (visible alongside Simulate PIM). 'auto' = no
    // override (current user's bypass-Unlimited applies); any other value is a real
    // plan ID sent in create-session as `simulatedPlanId`. The backend honors the
    // override only when the session has billing bypass (HasBillingBypass=true) —
    // non-bypass callers cannot escalate by setting it.
    const [simulatedPlanId, setSimulatedPlanId] = useState('auto');
    const [password, setPassword] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isEmailCopied, setIsEmailCopied] = useState(false);
    const [isAiSummaryCopied, setIsAiSummaryCopied] = useState(false);
    const [showSourceText, setShowSourceText] = useState(false);

    // Environment & URLs
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const [serverUrl] = useState(process.env.REACT_APP_WEBSOCKET_URL || (isLocalDev ? 'wss://localhost:7125' : 'wss://websocket-test.qolsync.ai'));
    // Derived Functions URL — must match the WebSocket server's environment so that
    // create-session writes to the same Redis instance that the WS server reads from.
    // Both halves of the answer, deliberately. `resolved: false` means the URL below is the
    // hard-coded staging fall-through rather than a backend anyone configured — the host would
    // otherwise create sessions, list tenants and download transcripts against staging while the
    // operator believes they are on their own backend, which is the failure this module's header
    // describes. Reading only `baseUrl` is what hid it.
    const { baseUrl: functionsBaseUrl, resolved: functionsTargetResolved } = resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        isLocalDev
    );

    // Advanced Settings
    const [hostInstructions, setHostInstructions] = useState('');
    const [voiceType] = useState('male');
    const [useSemanticSegmentation, setUseSemanticSegmentation] = useState(true);
    const [canUnifyModel, setCanUnifyModel] = useState(true);
    // 'Auto' = no explicit override; server's LanguageRoutingService picks the best provider
    // based on source language (en-IN/PH/NG/ZA -> AssemblyAI, ko-KR/ja-JP -> Google,
    // zh-CN / zh-SG / vi-VN / id-ID / ar-* / ms-MY / ta-IN -> Soniox, en-* -> Speechmatics,
    // th-TH and everything else -> DeepGram). Recommended default per Sprint 2 routing config.
    const [transcriber, setTranscriber] = useState('Auto');
    const [translator, setTranslator] = useState('OpenAI');
    // 'Auto' = no explicit model override; server's TranslationPipeline picks the
    // best model from PartialModelChains / FinalModelChains in appsettings (currently
    // gpt-4.1-nano partials, gpt-5.2 finals for OpenAI). Recommended default.
    const [model, setModel] = useState('Auto');
    const [customModelInput, setCustomModelInput] = useState('');
    const [urlCustomModel] = useState('');
    const [interpreterMode, setInterpreterMode] = useState(false);
    const [segmentationSilenceTimeout, setSegmentationSilenceTimeout] = useState(-1);
    const [googleSttModel, setGoogleSttModel] = useState('');
    const [singleUtterance, setSingleUtterance] = useState(false);
    const [useSentenceBuffering, setUseSentenceBuffering] = useState(true);
    const [speechmaticsMaxDelay, setSpeechmaticsMaxDelay] = useState(2.0);
    const [enableV2PostProcessing, setEnableV2PostProcessing] = useState(true);
    // GptRealtimeWhisper per-session overrides. Empty string = no override
    // (server uses appsettings default, falling back to OpenAI's server-side default).
    // gptRealtimeWhisperDelay is an enum string ("minimal"/"low"/"medium"/"high"/"xhigh"),
    // not an int — OpenAI changed the contract on 2026-05-11. See TranslationSessionContext.GptRealtimeWhisper*.
    const [gptRealtimeWhisperModel, setGptRealtimeWhisperModel] = useState('');
    const [gptRealtimeWhisperDelay, setGptRealtimeWhisperDelay] = useState('');
    const [gptRealtimeWhisperIncludeLogprobs, setGptRealtimeWhisperIncludeLogprobs] = useState(true);
    // Commit cadence (silence-based + safety net). Empty string = use server default.
    // Per-session tunables for the manual input_audio_buffer.commit logic that this
    // provider needs because OpenAI's transcription endpoint has no server VAD.
    const [gptRealtimeWhisperCommitSilenceMs, setGptRealtimeWhisperCommitSilenceMs] = useState('');
    const [gptRealtimeWhisperCommitMaxIntervalMs, setGptRealtimeWhisperCommitMaxIntervalMs] = useState('');
    // OpenAI input_audio_noise_reduction preset. '' = no override (omit field → OpenAI default off).
    // 'near_field' = close-talking mics (headsets); 'far_field' = distant mics (laptops/conference rooms).
    const [gptRealtimeWhisperNoiseReduction, setGptRealtimeWhisperNoiseReduction] = useState('');
    // Client-side silence detector amplitude threshold (mean abs PCM16, range 0-10000).
    // Empty string = no override; backend appsettings default is 300.
    const [gptRealtimeWhisperCommitSilenceAmplitude, setGptRealtimeWhisperCommitSilenceAmplitude] = useState('');
    // Soniox per-session overrides (only honored when transcriber === 'Soniox').
    // Server SonioxOptions defaults: diarization/language-ID/endpoint-detection all true,
    // maxEndpointDelayMs 2000 (range 500-3000), translationMode EngineChain. Each control
    // emits a query param only when it differs from the server default, so the URL stays clean.
    // 'translationMode' '' = server default (EngineChain). 'SonioxNative' translates inside the
    // STT connection for a single target language; extra targets fall back to EngineChain.
    const [sonioxTranslationMode, setSonioxTranslationMode] = useState('');
    const [sonioxEnableSpeakerDiarization, setSonioxEnableSpeakerDiarization] = useState(true);
    const [sonioxEnableLanguageIdentification, setSonioxEnableLanguageIdentification] = useState(true);
    // Finalization strategy (D1): 'endpoint' (default) = automatic endpoint detection;
    // 'manual' = endpoint detection off, session relies on explicit {"type":"finalize"} triggers
    // (server-side RequestManualFinalizeAsync, Sprint S1). 'manual' was previously zero-output
    // before S1 shipped. 'endpoint' stays the safe default for all meeting sessions.
    // Empty string = no override (server default 2000 ms). Range 500-3000; only shown in endpoint mode.
    const [sonioxMaxEndpointDelayMs, setSonioxMaxEndpointDelayMs] = useState('');
    // Comma-separated ISO codes (e.g. "en,ko,ja") biasing Soniox language detection. '' = none.
    const [sonioxLanguageHints, setSonioxLanguageHints] = useState('');
    const [longSentenceModeSeconds, setLongSentenceModeSeconds] = useState(0);
    const [confidenceThreshold, setConfidenceThreshold] = useState(0.0);

    // V2 Engine Settings
    const [engineVersion, setEngineVersion] = useState('v2');
    // 'Auto' = no explicit override; server uses ParallelModelExecutorV2 default
    // chain (gpt-5.2 primary + gpt-5-mini / gpt-5.6-luna / gpt-4.1-mini fallbacks per
    // appsettings TranslationPerformance.FinalModelChains).
    const [v2PrimaryModel, setV2PrimaryModel] = useState('Auto');
    const [v2PrimaryTimeout, setV2PrimaryTimeout] = useState(5);
    // Per-session OpenAI reasoning_effort override for gpt-5.x finals (V2 only).
    // 'default' sends no query param — server falls back to its ModelRouting:ReasoningEffort
    // appsettings default. Other values (minimal/low/medium/high) override for THIS session.
    const [reasoningEffort, setReasoningEffort] = useState('default');
    // Per-session rolling summary override: 'default' | 'on' | 'off'.
    // 'default' sends no query param — server falls back to its V2PipelineModels.RollingSummary.Enabled
    // appsettings flag. 'on'/'off' force the feature for THIS session only (overrides server config).
    // Used for A/B testing without restarting TranslationEngine.
    const [rollingSummaryOverride, setRollingSummaryOverride] = useState('default');

    // Session-type scenario classification — drives system-prompt presets and
    // (downstream) STT default tuning. 'Conversation' = no preset (default behavior).
    // Other options inject scenario-specific guidance into the translator's system prompt.
    const [sessionType, setSessionType] = useState('Conversation');

    // Tenant & Glossary State
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [tenantGlossaries, setTenantGlossaries] = useState([]);
    const [selectedGlossary, setSelectedGlossary] = useState('');
    const [sharedGlossaries, setCategoryGlossaries] = useState([]);
    const [selectedSharedGlossary, setSelectedSharedGlossary] = useState('');

    // UI State
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('general'); // general, advanced, audio
    // eslint-disable-next-line no-unused-vars
    const [statusMessage, setStatusMessage] = useState('Configure session settings to get started');
    // eslint-disable-next-line no-unused-vars
    const [statusType, setStatusType] = useState('info');
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isSessionPaused, setIsSessionPaused] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [signalRConnected, setSignalRConnected] = useState(false);
    const [isSignalRDegraded, setIsSignalRDegraded] = useState(false);
    const [translations, setTranslations] = useState([]);
    const [unifiedTranslations, setUnifiedTranslations] = useState([]);
    const [currentPartial, setCurrentPartial] = useState('');
    const [isStartingSession, setIsStartingSession] = useState(false);
    const [isStoppingSession, setIsStoppingSession] = useState(false);
    const [connectionError, setConnectionError] = useState('');
    const [hasEverConnected, setHasEverConnected] = useState(false);

    // Free Tier Usage Tracking
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);

    // Debug State
    const [debugMode, setDebugMode] = useState(false);
    const [logs, setLogs] = useState([]);

    // Audio State
    const [mediaStream, setMediaStream] = useState(null);
    const [audioContext, setAudioContext] = useState(null);
    const [processorNode, setProcessorNode] = useState(null);
    const [sourceNode, setSourceNode] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [webSocket, setWebSocket] = useState(null);
    const [audioLevel, setAudioLevel] = useState(0);

    // Refs
    const isRecordingRef = useRef(false);
    const webSocketRef = useRef(null);
    const translationOutputRef = useRef(null);
    const reconnectTimeoutRef = useRef(null); // For WebSocket reconnection
    const reconnectAttemptsRef = useRef(0); // Track reconnection attempts
    const canAutoScrollRef = useRef(true); // Track if auto-scroll is enabled (user at bottom)
    const audioDebugLoggedRef = useRef(false); // Log audio sample rate once

    // Fix for Stale Closure in Callbacks: Keep a ref synced with state
    const isSessionActiveRef = useRef(isSessionActive);
    useEffect(() => {
        isSessionActiveRef.current = isSessionActive;
    }, [isSessionActive]);

    // Close download menu on outside click
    useEffect(() => {
        if (!showDownloadMenu) return;
        const handleOutsideClick = (e) => {
            if (!(e.target instanceof Element)) return;
            if (!e.target.closest('.download-dropdown')) setShowDownloadMenu(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showDownloadMenu]);

    // Model Options
    const translatorModels = {
        'OpenAI': [
            { value: 'gpt-5.1', label: 'GPT-5.1 ⭐️ Default & Latest' },
            { value: 'gpt-4o', label: 'GPT-4o 🔥 Popular' },

            { value: 'gpt-4.1', label: 'GPT-4.1' },
            { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
            { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
            { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
            { value: 'custom', label: '🔧 Custom Model' }
        ],
        'AzureOpenAI': [
            { value: 'gpt-5.1', label: 'GPT-5.1 ⭐️ Default & Latest' },
            { value: 'gpt-4o', label: 'GPT-4o 🔥 Popular' },
            { value: 'gpt-4.1', label: 'GPT-4.1' },
            { value: 'custom', label: '🔧 Custom Model' }
        ],
        'Gemini': [
            { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
            { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite ⚡️' },
            { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite' },
            { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
            { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro ⭐️' },
            { value: 'custom', label: '🔧 Custom Model' }
        ],
        'Google Translate': [],
        'DeepSeek': [
            { value: 'deepseek-chat', label: 'DeepSeek Chat' },
            { value: 'deepseek-coder', label: 'DeepSeek Coder' },
            { value: 'custom', label: '🔧 Custom Model' }
        ],
        'DeepL': [],
        'AzureAITranslator': []
    };

    // Initialize Session ID and parse URL params
    useEffect(() => {
        if (!sessionId.trim()) {
            setSessionId(`${Math.floor(Math.random() * 90000) + 10000}`);
        }
    }, [sessionId]);

    // Initialize Tenant Info
    // Check if user can select tenants (must match the dropdown visibility condition)
    const canSelectTenant = user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin' || user?.Role === 'Owner';

    useEffect(() => {
        if (user) {
            if (canSelectTenant) {
                // Fetch all tenants for dropdown using the new protected endpoint (Sprint 3 migration)
                fetchWithAuth(`${functionsBaseUrl}/superadmin/tenants`, {
                    method: 'GET'
                })
                    .then(res => res.json())
                    .then(data => {
                        // AdminListTenants returns { count, tenants: [{tenantId, name, ...}] }
                        // Normalize to legacy shape [{ RowKey, TenantName, Metadata, ... }] used by this component
                        const tenantList = (data && Array.isArray(data.tenants))
                            ? data.tenants.map(t => ({
                                RowKey: t.tenantId,
                                TenantName: t.name,
                                OrganizationName: t.organizationName,
                                LifecycleState: t.lifecycleState,
                                CreatedAt: t.createdAt,
                                PlanTier: t.planTier,
                                UserCount: t.userCount,
                                Metadata: t.metadata
                            }))
                            : (Array.isArray(data) ? data : []);
                        // Filter out integration test data (Metadata.source === 'integration-test')
                        const filteredTenants = tenantList.filter(t => t.Metadata?.source !== 'integration-test');
                        setTenants(filteredTenants);
                    })
                    .catch(err => console.error('Failed to fetch tenants:', err));
            } else if (user.TenantId) {
                // Pre-select assigned tenant
                setSelectedTenantId(user.TenantId);
                setTenantName(user.TenantName);
            }
        }
    }, [user, canSelectTenant, functionsBaseUrl]);

    // Load preferences
    useEffect(() => {
        const savedShowSourceText = localStorage.getItem('showSourceText');
        if (savedShowSourceText) setShowSourceText(savedShowSourceText === 'true');
    }, []);

    // Fetch Glossaries when Tenant Changes (uses JWT authentication)
    useEffect(() => {
        const fetchGlossaries = async () => {
            // Check if JWT token is available
            if (!getJwtToken()) {
                console.log('[Glossary] No JWT token available yet, skipping glossary fetch');
                return;
            }

            console.log('[Glossary] Fetching glossaries with JWT authentication');

            // Fetch Category Glossaries (Always available for all users)
            try {
                console.log('[Glossary] Fetching category glossaries...');
                const response = await glossaryService.listCategoryGlossaries();
                console.log('[Glossary] Category glossaries response:', response);
                setCategoryGlossaries(response.glossaries || []);
            } catch (e) {
                console.error('[Glossary] Error fetching category glossaries:', e);
            }

            // Fetch Tenant Glossaries if Tenant Selected
            if (selectedTenantId) {
                try {
                    console.log('[Glossary] Fetching tenant glossaries for:', selectedTenantId);
                    const response = await glossaryService.listGlossaries(selectedTenantId);
                    console.log('[Glossary] Tenant glossaries response:', response);
                    setTenantGlossaries(response['tenant-glossaries'] || response.glossaries || []);
                } catch (e) {
                    console.error('[Glossary] Error fetching tenant glossaries:', e);
                    setTenantGlossaries([]);
                }
            } else {
                setTenantGlossaries([]);
            }
        };

        fetchGlossaries();
    }, [selectedTenantId]);

    // Clear tenant glossary selection when tenant changes
    useEffect(() => {
        setSelectedGlossary('');
    }, [selectedTenantId]);

    // Handle scroll position detection (pause auto-scroll when user scrolls up)
    useEffect(() => {
        const handleScroll = () => {
            if (!translationOutputRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = translationOutputRef.current;
            // User is at bottom if within 100px of scrollHeight (increased tolerance)
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
            canAutoScrollRef.current = isAtBottom;
        };

        const container = translationOutputRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Scroll to bottom (only if user is at bottom / auto-scroll enabled)
    useEffect(() => {
        if (canAutoScrollRef.current && translationOutputRef.current) {
            // Use requestAnimationFrame to ensure layout is complete before scrolling
            requestAnimationFrame(() => {
                if (translationOutputRef.current) {
                    translationOutputRef.current.scrollTop = translationOutputRef.current.scrollHeight;
                }
            });
        }
    }, [translations, currentPartial, unifiedTranslations]);

    // Helper: Get Final Model
    // Returns '' for 'Auto' so the URL builder omits the model param and the
    // server-side TranslationPipeline falls back to its configured chain.
    const getFinalModel = () => {
        if (urlCustomModel.trim()) return urlCustomModel.trim();
        if (model === 'custom' && customModelInput.trim()) return customModelInput.trim();
        if (model === 'Auto') return '';
        return model;
    };

    // Helper: Handle Translator Change
    // Reset model to 'Auto' when translator changes — let the server pick from
    // the new translator's model chain rather than picking the first hardcoded
    // option that may not be the tuned default.
    const handleTranslatorChange = (newTranslator) => {
        setTranslator(newTranslator);
        setModel('Auto');
        setCustomModelInput('');
    };

    // Helper: Add Log
    const addLog = useCallback((msg, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [`[${timestamp}] ${msg}`, ...prev].slice(0, 200));
        // We use a specific tag so the interceptor can ignore this and avoid double-logging
        // But we still want it in the browser console
        if (window.originalConsoleLog) {
            window.originalConsoleLog(`[AppDebug] ${msg}`);
        } else {
            console.log(`[AppDebug] ${msg}`);
        }
    }, []);

    // Global Console & Error Interceptor (Always Active)
    useEffect(() => {
        // Save originals
        const originalLog = console.log;
        const originalWarn = console.warn;
        const originalError = console.error;

        // Store on window for addLog to use safely
        window.originalConsoleLog = originalLog;

        const formatArgs = (args) => {
            return args.map(arg => {
                if (arg instanceof Error) {
                    return `${arg.message}\n${arg.stack}`;
                }
                if (typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg);
                    } catch (e) {
                        return String(arg);
                    }
                }
                return String(arg);
            }).join(' ');
        };

        const intercept = (originalFn, type) => (...args) => {
            originalFn.apply(console, args);

            const msg = formatArgs(args);

            // Ignore [AppDebug] logs to avoid duplication since addLog handles them
            if (msg.includes('[AppDebug]')) return;

            const timestamp = new Date().toLocaleTimeString();
            setLogs(prev => [`[${timestamp}] [${type.toUpperCase()}] ${msg}`, ...prev].slice(0, 200));
        };

        console.log = intercept(originalLog, 'log');
        console.warn = intercept(originalWarn, 'warn');
        console.error = intercept(originalError, 'error');

        // Capture Global Errors (Script errors, etc.)
        const handleGlobalError = (event, source, lineno, colno, error) => {
            addLog(`Global Error: ${event} at ${source}:${lineno}:${colno}\n${error?.stack || ''}`, 'error');
        };
        window.onerror = handleGlobalError;

        // Capture Unhandled Promise Rejections (Network failures, async errors)
        const handlePromiseRejection = (event) => {
            addLog(`Unhandled Promise Rejection: ${event.reason?.message || event.reason}`, 'error');
        };
        window.addEventListener('unhandledrejection', handlePromiseRejection);

        return () => {
            console.log = originalLog;
            console.warn = originalWarn;
            console.error = originalError;
            delete window.originalConsoleLog;
            window.onerror = null;
            window.removeEventListener('unhandledrejection', handlePromiseRejection);
        };
    }, [addLog]);

    // Monitoring: Visibility State
    useEffect(() => {
        const handleVisibilityChange = () => {
            const state = document.visibilityState;
            addLog(`App visibility changed: ${state}`, state === 'visible' ? 'success' : 'warning');

            // Auto-resume audio on visibility restore
            if (state === 'visible' && isSessionActiveRef.current && audioContext && (audioContext.state === 'suspended' || audioContext.state === 'interrupted')) {
                addLog('App visible - attempting audio resume...', 'warning');
                audioContext.resume().catch(e => console.error('Resume failed:', e));
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [addLog, audioContext]);



    // Wake Lock for iOS/Mobile
    useEffect(() => {
        let wakeLock = null;
        const requestWakeLock = async () => {
            if ('wakeLock' in navigator) {
                try {
                    wakeLock = await navigator.wakeLock.request('screen');
                    addLog('Screen Wake Lock active', 'success');
                } catch (err) {
                    addLog(`Wake Lock failed: ${err.name}, ${err.message}`, 'error');
                }
            }
        };

        if (isSessionActive) {
            requestWakeLock();
        }

        return () => {
            if (wakeLock) wakeLock.release();
        };
    }, [isSessionActive, addLog]);

    // Helper: Handle Transcriber Change
    const handleTranscriberChange = (newTranscriber) => {
        setTranscriber(newTranscriber);
        if (newTranscriber !== 'Microsoft') {
            if (interpreterMode) setInterpreterMode(false);
            if (useSemanticSegmentation) setUseSemanticSegmentation(false);
            setSegmentationSilenceTimeout(-1);
        }
    };



    // Helper: Pause Session
    const callPauseSession = useCallback(async () => {
        try {
            if (!getJwtToken()) return;

            const functionUrl = functionsBaseUrl;
            const response = await fetchWithAuth(
                `${functionUrl}/translation/pause-session?sessionId=${encodeURIComponent(sessionId)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (!response.ok) throw new Error('Pause failed');

            isRecordingRef.current = false;
            setIsSessionPaused(true);
            setStatusMessage('Session paused (Interrupted)');
            setStatusType('warning');
            return true;
        } catch (error) {
            console.error('Error pausing session:', error);
            setStatusMessage('Failed to pause session');
            setStatusType('error');
            return false;
        }
    }, [sessionId, functionsBaseUrl]);

    // Helper: Resume Session
    const callResumeSession = useCallback(async () => {
        try {
            if (!getJwtToken()) return;

            const functionUrl = functionsBaseUrl;
            const response = await fetchWithAuth(
                `${functionUrl}/translation/resume-session?sessionId=${encodeURIComponent(sessionId)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (!response.ok) throw new Error('Resume failed');

            isRecordingRef.current = true;
            setIsSessionPaused(false);
            setStatusMessage('Broadcasting live...');
            setStatusType('success');
            return true;
        } catch (error) {
            console.error('Error resuming session:', error);
            setStatusMessage('Failed to resume session');
            setStatusType('error');
            return false;
        }
    }, [sessionId, functionsBaseUrl]);

    // Helper: Download Transcript
    const downloadTranscript = () => {
        const dataToDownload = unifiedTranslations.length > 0 ? unifiedTranslations : translations;

        if (dataToDownload.length === 0) {
            addLog('No transcript data to download', 'warning');
            return;
        }

        // CSV Header
        let csvContent = "Timestamp,Speaker,Source Text,Translated Text\n";

        // CSV Rows
        dataToDownload.forEach(item => {
            if (!item.isFinal && !item.text) return; // Skip empty partials

            const timestamp = item.timestamp ? new Date(item.timestamp).toLocaleTimeString() : '';
            const speaker = item.speaker || 'Unknown';
            // Escape quotes in text by doubling them, wrap fields in quotes
            const sourceText = `"${(item.sourceText || '').replace(/"/g, '""')}"`;
            const translatedText = `"${(item.text || '').replace(/"/g, '""')}"`;

            csvContent += `${timestamp},${speaker},${sourceText},${translatedText}\n`;
        });

        // Create Blob with BOM for UTF-8 support (Critical for CJK characters in Excel)
        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        // Trigger Download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `transcript_${sessionId}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        addLog('Transcript downloaded', 'success');
    };

    // Helper: Download Plain Text or Raw Text Transcript
    // format='plaintext' → speaker-grouped ([Speaker] headers), format='rawtext' → pure text only
    const downloadPlainText = async (langOrSource, format = 'plaintext') => {
        if (!sessionId) {
            addLog('No session ID available for download', 'warning');
            return;
        }

        const isSource = langOrSource === 'source';
        const url = isSource
            ? `${functionsBaseUrl}/meeting-ai/transcript/download?sessionId=${encodeURIComponent(sessionId)}&format=${format}`
            : `${functionsBaseUrl}/meeting-ai/transcript/translations?sessionId=${encodeURIComponent(sessionId)}&language=${encodeURIComponent(langOrSource)}&format=${format}`;

        try {
            const response = await fetchWithAuth(url, { method: 'GET' });

            if (!response.ok) {
                addLog(`Failed to download plain text transcript: ${response.status}`, 'error');
                return;
            }

            const text = await response.text();
            const suffix = format === 'rawtext' ? '-raw' : '';
            const fileName = isSource
                ? `transcript-${sessionId}-source${suffix}.txt`
                : `transcript-${sessionId}-${langOrSource}${suffix}.txt`;

            const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);

            addLog(`Plain text transcript downloaded: ${fileName}`, 'success');
        } catch (err) {
            addLog(`Error downloading plain text transcript: ${err.message}`, 'error');
            trackException(err, { sessionId, errorType: 'TranslationHost_PlainTextDownloadFailed' });
        }
    };

    // Audio Initialization
    const initializeAudio = async () => {
        try {
            console.log('[Host] Initializing audio with processed constraints...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,      // CHANGED: Enable for less sensitivity/feedback
                    noiseSuppression: true,      // CHANGED: Enable to reduce background noise
                    autoGainControl: true        // CHANGED: Enable for consistent levels
                }
            });

            // Mobile Interruption Handling (New)
            const track = stream.getAudioTracks()[0];
            if (track) {
                track.onended = () => {
                    console.warn('[Host] Audio track ended unexpectedly (OS killed it or device unplugged)');
                    addLog('Microphone disconnected by system', 'error');

                    // If session is still active, we must recover. 
                    // Simple recovery: Stop session to prevent "dead air" state.
                    if (isSessionActiveRef.current) {
                        setStatusMessage('Microphone disconnected - Please restart session');
                        setStatusType('error');
                    }
                };

                track.onmute = () => {
                    console.log('[Host] Microphone muted by system (e.g. phone call)');
                    addLog('Microphone interrupted (call/background)', 'warning');
                    if (isSessionActiveRef.current) {
                        callPauseSession().catch(console.error);
                    }
                };

                track.onunmute = () => {
                    console.log('[Host] Microphone unmuted by system');
                    addLog('Microphone resumed', 'success');
                    if (isSessionActiveRef.current) {
                        callResumeSession().catch(console.error);
                    }
                };
            }

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioContextClass({ sampleRate: 16000 });
            // Note: browsers may ignore requested sampleRate; log the actual rate for debugging.
            console.log('[Host][Audio] AudioContext created:', {
                requestedSampleRate: 16000,
                actualSampleRate: audioCtx.sampleRate,
                baseLatency: audioCtx.baseLatency,
                outputLatency: audioCtx.outputLatency
            });
            addLog(`[Audio] AudioContext sampleRate=${audioCtx.sampleRate} (requested 16000)`, audioCtx.sampleRate === 16000 ? 'success' : 'warning');

            const source = audioCtx.createMediaStreamSource(stream);
            const processor = audioCtx.createScriptProcessor(4096, 1, 1);

            // Resampling function: downsample from sourceSampleRate to targetSampleRate (16kHz)
            const TARGET_SAMPLE_RATE = 16000;
            const resampleTo16kHz = (inputData, sourceSampleRate) => {
                if (sourceSampleRate === TARGET_SAMPLE_RATE) {
                    // No resampling needed
                    return inputData;
                }

                const ratio = sourceSampleRate / TARGET_SAMPLE_RATE;
                const outputLength = Math.floor(inputData.length / ratio);
                const output = new Float32Array(outputLength);

                // Linear interpolation resampling
                for (let i = 0; i < outputLength; i++) {
                    const srcIndex = i * ratio;
                    const srcIndexFloor = Math.floor(srcIndex);
                    const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
                    const t = srcIndex - srcIndexFloor;

                    // Linear interpolation between two samples
                    output[i] = inputData[srcIndexFloor] * (1 - t) + inputData[srcIndexCeil] * t;
                }

                return output;
            };

            // Track the actual sample rate for resampling
            let actualSampleRate = audioCtx.sampleRate;

            processor.onaudioprocess = (event) => {
                if (!audioDebugLoggedRef.current) {
                    audioDebugLoggedRef.current = true;
                    actualSampleRate = event.inputBuffer?.sampleRate || audioCtx.sampleRate;
                    console.log('[Host][Audio] First onaudioprocess:', {
                        inputBufferSampleRate: actualSampleRate,
                        frameCount: event.inputBuffer?.length,
                        channelCount: event.inputBuffer?.numberOfChannels,
                        needsResampling: actualSampleRate !== TARGET_SAMPLE_RATE,
                        resampleRatio: actualSampleRate / TARGET_SAMPLE_RATE
                    });
                    if (actualSampleRate !== TARGET_SAMPLE_RATE) {
                        addLog(`[Audio] Resampling from ${actualSampleRate}Hz to ${TARGET_SAMPLE_RATE}Hz (ratio: ${(actualSampleRate / TARGET_SAMPLE_RATE).toFixed(2)}x)`, 'warning');
                    } else {
                        addLog(`[Audio] Sample rate is ${actualSampleRate}Hz (no resampling needed)`, 'success');
                    }
                }
                if (isRecordingRef.current && webSocketRef.current?.readyState === WebSocket.OPEN) {
                    const inputData = event.inputBuffer.getChannelData(0);

                    // Calculate audio level for visualization (before resampling)
                    const rms = Math.sqrt(inputData.reduce((sum, sample) => sum + sample * sample, 0) / inputData.length);
                    setAudioLevel(Math.min(rms * 5, 1)); // Scale for display

                    // Resample to 16kHz if needed
                    const resampledData = resampleTo16kHz(inputData, actualSampleRate);

                    // Convert to 16-bit PCM
                    const int16Data = new Int16Array(resampledData.length);
                    for (let i = 0; i < resampledData.length; i++) {
                        int16Data[i] = Math.max(-32768, Math.min(32767, resampledData[i] * 32767));
                    }
                    webSocketRef.current.send(int16Data.buffer);
                }
            };

            source.connect(processor);

            // Create a gain node with 0 gain to mute the output (prevents feedback)
            // while keeping the audio graph connected (required for ScriptProcessor to fire)
            const muteNode = audioCtx.createGain();
            muteNode.gain.value = 0;

            processor.connect(muteNode);
            muteNode.connect(audioCtx.destination);

            setMediaStream(stream);
            setAudioContext(audioCtx);
            setProcessorNode(processor);
            setSourceNode(source);

            // iOS Fix: Resume AudioContext if suspended (common on iOS)
            if (audioCtx.state === 'suspended') {
                await audioCtx.resume();
                addLog('AudioContext resumed (iOS fix)', 'warning');
            }

            // Monitor AudioContext state
            audioCtx.onstatechange = () => {
                const state = audioCtx.state;
                addLog(`Main AudioCtx state: ${state}`, state === 'running' ? 'success' : 'warning');

                // Auto-resume if interrupted (iOS/Android call ended)
                if ((state === 'suspended' || state === 'interrupted') && isSessionActiveRef.current) {
                    addLog('Audio interrupted, checking state...', 'warning');

                    // Pause session on server to prevent timeout
                    callPauseSession().catch(console.error);

                    // Attempt auto-resume
                    addLog('Attempting auto-resume...', 'warning');
                    audioCtx.resume().then(() => {
                        // If resume successful, tell server to resume
                        if (isSessionActiveRef.current) {
                            callResumeSession().catch(console.error);
                        }
                    }).catch(e => console.error('Resume failed:', e));
                }
            };

            return true;
        } catch (error) {
            console.error('Audio init failed:', error);
            addLog(`Audio init failed: ${error.message}`, 'error');
            trackException(error, {
                sessionId,
                errorType: 'TranslationHost_AudioInitFailed'
            });
            setStatusMessage('Microphone access denied');
            setStatusType('error');
            return false;
        }
    };

    // WebSocket Initialization
    const initializeWebSocket = async () => {
        try {
            const targetLanguagesParam = encodeURIComponent(targetLanguages.join(','));
            const baseUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
            const endpoint = `${baseUrl}/audio-stream`;

            console.log('[Host] Initializing WebSocket:', {
                serverUrl,
                baseUrl,
                endpoint
            });

            // Get JWT token for WebSocket authentication
            const jwtToken = getJwtToken();
            if (!jwtToken) {
                console.error('[Host] JWT token not available for WebSocket connection');
                throw new Error('JWT token not available for WebSocket connection');
            }

            console.log('[Host] JWT token available:', {
                tokenLength: jwtToken.length,
                tokenPreview: jwtToken.substring(0, 50) + '...'
            });

            // Get trace ID for distributed tracing
            const traceId = getCurrentTraceId();

            const queryParams = [
                `sessionId=${encodeURIComponent(sessionId)}`,
                `sourceLanguage=${encodeURIComponent(sourceLanguage)}`,
                `targetLanguages=${targetLanguagesParam}`,
                `token=${encodeURIComponent(jwtToken)}`,
                `enablePartial=${enablePartialText}`,
                `enableTts=${enableTts}`,
                `voiceType=${encodeURIComponent(voiceType)}`,
                `useSemanticSegmentation=${useSemanticSegmentation}`,
                // 'Auto' means: omit the transcriber param so the server's
                // LanguageRoutingService picks the provider based on source language.
                ...(transcriber !== 'Auto' ? [`transcriber=${encodeURIComponent(transcriber)}`] : []),
                `translator=${encodeURIComponent(translator)}`,
                `canUnifyModel=${canUnifyModel}`,
                `confidenceThreshold=${confidenceThreshold}`,
                `useSentenceBuffering=${useSentenceBuffering}`
            ];

            // Add partialTextMode when partial text is enabled
            if (enablePartialText) {
                queryParams.push(`partialTextMode=${encodeURIComponent(partialTextMode)}`);
            }

            // Add trace ID for distributed tracing correlation
            if (traceId) {
                queryParams.push(`traceId=${encodeURIComponent(traceId)}`);
            }

            if (selectedTenantId) queryParams.push(`tenantId=${encodeURIComponent(selectedTenantId)}`);
            // Only include tenant glossary if a tenant is selected
            if (selectedTenantId && selectedGlossary) queryParams.push(`tenantGlossaryName=${encodeURIComponent(selectedGlossary)}`);
            if (selectedSharedGlossary) queryParams.push(`sharedGlossaryName=${encodeURIComponent(selectedSharedGlossary)}`);

            const finalModel = getFinalModel();
            if (finalModel) queryParams.push(`model=${encodeURIComponent(finalModel)}`);
            if (hostInstructions) queryParams.push(`hostInstructions=${encodeURIComponent(hostInstructions)}`);

            // V2 Engine parameters
            if (engineVersion) queryParams.push(`engineVersion=${encodeURIComponent(engineVersion)}`);
            if (engineVersion === 'v2') {
                // 'Auto' means: omit v2PrimaryModel so ParallelModelExecutorV2 uses
                // its configured chain from appsettings (FinalModelChains).
                if (v2PrimaryModel !== 'Auto') {
                    queryParams.push(`v2PrimaryModel=${encodeURIComponent(v2PrimaryModel)}`);
                }
                queryParams.push(`v2PrimaryTimeout=${v2PrimaryTimeout}`);

                // Per-session reasoning_effort override (V2 only).
                // 'default' emits nothing — server uses its ModelRouting:ReasoningEffort config.
                if (reasoningEffort && reasoningEffort !== 'default') {
                    queryParams.push(`reasoningEffort=${encodeURIComponent(reasoningEffort)}`);
                }

                // Per-session rolling summary override (V2 only).
                // Only emit when explicitly overridden — 'default' lets server config decide.
                if (rollingSummaryOverride === 'on') {
                    queryParams.push(`enableRollingSummary=true`);
                } else if (rollingSummaryOverride === 'off') {
                    queryParams.push(`enableRollingSummary=false`);
                }

                // Session-type scenario preset (V2 only).
                // Only emit when not the default Conversation — keeps URL clean.
                if (sessionType && sessionType !== 'Conversation') {
                    queryParams.push(`sessionType=${encodeURIComponent(sessionType)}`);
                }
            }

            // Speechmatics-specific parameters
            if (transcriber === 'Speechmatics') {
                queryParams.push(`speechmaticsMaxDelay=${speechmaticsMaxDelay}`);
                if (enableV2PostProcessing) {
                    queryParams.push(`enableV2PostProcessing=true`);
                }
            }

            // GptRealtimeWhisper-specific per-session overrides. Each is emitted only when
            // explicitly set so the server's appsettings default applies otherwise.
            // Model: empty string = no override (blank input).
            // Delay: empty string = no override (server-default dropdown choice).
            // IncludeLogprobs: only emit when explicitly disabled (default is true).
            // CommitSilenceMs / CommitMaxIntervalMs: empty string = no override.
            if (transcriber === 'GptRealtimeWhisper') {
                if (gptRealtimeWhisperModel && gptRealtimeWhisperModel.trim() !== '') {
                    queryParams.push(`gptRealtimeWhisperModel=${encodeURIComponent(gptRealtimeWhisperModel.trim())}`);
                }
                if (gptRealtimeWhisperDelay && gptRealtimeWhisperDelay !== '') {
                    queryParams.push(`gptRealtimeWhisperDelay=${encodeURIComponent(gptRealtimeWhisperDelay)}`);
                }
                if (!gptRealtimeWhisperIncludeLogprobs) {
                    queryParams.push(`gptRealtimeWhisperIncludeLogprobs=false`);
                }
                if (gptRealtimeWhisperCommitSilenceMs !== '' && Number.isFinite(parseInt(gptRealtimeWhisperCommitSilenceMs, 10))) {
                    queryParams.push(`gptRealtimeWhisperCommitSilenceMs=${parseInt(gptRealtimeWhisperCommitSilenceMs, 10)}`);
                }
                if (gptRealtimeWhisperCommitMaxIntervalMs !== '' && Number.isFinite(parseInt(gptRealtimeWhisperCommitMaxIntervalMs, 10))) {
                    queryParams.push(`gptRealtimeWhisperCommitMaxIntervalMs=${parseInt(gptRealtimeWhisperCommitMaxIntervalMs, 10)}`);
                }
                if (gptRealtimeWhisperNoiseReduction && gptRealtimeWhisperNoiseReduction !== '') {
                    queryParams.push(`gptRealtimeWhisperNoiseReduction=${encodeURIComponent(gptRealtimeWhisperNoiseReduction)}`);
                }
                if (gptRealtimeWhisperCommitSilenceAmplitude !== '' && Number.isFinite(parseInt(gptRealtimeWhisperCommitSilenceAmplitude, 10))) {
                    queryParams.push(`gptRealtimeWhisperCommitSilenceAmplitudeThreshold=${parseInt(gptRealtimeWhisperCommitSilenceAmplitude, 10)}`);
                }
            }

            // Soniox-specific per-session overrides. Each is emitted only when it differs
            // from the server SonioxOptions default so the appsettings default applies otherwise.
            // Param names must match TranslationSessionContext query parsing exactly.
            if (transcriber === 'Soniox') {
                // Build Soniox-specific query params via the pure helper.
                const sonioxParams = buildSonioxQueryParams({
                    sonioxMaxEndpointDelayMs,
                    sonioxTranslationMode,
                    sonioxEnableSpeakerDiarization,
                    sonioxEnableLanguageIdentification,
                    sonioxLanguageHints,
                });
                sonioxParams.forEach((p) => queryParams.push(p));
            }

            // Long Sentence Mode — only when enabled (> 0)
            if (longSentenceModeSeconds > 0) {
                queryParams.push(`longSentenceModeSeconds=${longSentenceModeSeconds}`);
            }

            const wsUrl = `${endpoint}?${queryParams.join('&')}`;

            console.log('[Host] WebSocket URL constructed:', {
                url: wsUrl,
                paramCount: queryParams.length,
                hasToken: wsUrl.includes('token='),
                sessionId: sessionId
            });

            console.log('[Host] Creating WebSocket connection...');
            addLog(`Connecting WS to ${endpoint}...`);
            const ws = new WebSocket(wsUrl);

            // Wait for WebSocket to actually connect before returning
            // This prevents showing "OFFLINE" during initial connection
            const connectionPromise = new Promise((resolve, reject) => {
                const connectionTimeout = setTimeout(() => {
                    reject(new Error('WebSocket connection timeout (30s)'));
                }, 30000);

                ws.onopen = () => {
                    clearTimeout(connectionTimeout);
                    console.log('WebSocket Connected');
                    addLog('WebSocket Connected', 'success');
                    setIsConnected(true);
                    setHasEverConnected(true); // Mark that we've successfully connected
                    setConnectionError('');
                    isRecordingRef.current = true;
                    setStatusMessage('Broadcasting live...');
                    setStatusType('success');
                    console.log('[Host] WebSocket connected');
                    resolve(true);
                };

                ws.onerror = (error) => {
                    clearTimeout(connectionTimeout);
                    console.error('[Host] WebSocket Error:', {
                        error: error,
                        type: error.type,
                        target: error.target,
                        currentTarget: error.currentTarget,
                        readyState: ws.readyState,
                        url: ws.url,
                        sessionId: sessionId,
                        timestamp: new Date().toISOString()
                    });
                    addLog('WebSocket Error Occurred', 'error');
                    setConnectionError('WebSocket connection failed');
                    setStatusMessage('Connection error occurred');
                    setStatusType('error');
                    setIsConnected(false);
                    reject(new Error('WebSocket connection failed'));
                };
            });

            ws.onclose = (event) => {
                console.log('[Host] WebSocket Closed:', {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                    sessionId: sessionId,
                    timestamp: new Date().toISOString()
                });
                setIsConnected(false);
                isRecordingRef.current = false;

                // Check if this is an unexpected server-side closure
                const isServerTermination = event.reason === 'Translation session ended';
                const isNormalClosure = event.code === 1000 && event.wasClean;
                const wasSessionActive = isSessionActiveRef.current; // Use Ref to avoid stale closure

                console.log('[Host] Close analysis:', {
                    isServerTermination,
                    isNormalClosure,
                    wasSessionActive,
                    hasEverConnected
                });

                addLog(`WebSocket Closed: Code ${event.code}, Reason: ${event.reason || 'None'}`, 'error');

                // Set offline state and reset to allow restart
                if (wasSessionActive) {
                    if (isServerTermination) {
                        // Server ended the session - this is a valid termination, not an error
                        console.warn('[Host] Server ended session (valid termination):', {
                            code: event.code,
                            reason: event.reason,
                            sessionId
                        });
                        setStatusMessage('Session ended by server');
                        // Do not show error, just stop
                        stopSession();
                    } else if (isNormalClosure) {
                        // ... normal closure handling
                        const reason = event.reason || 'Connection closed by server';
                        setConnectionError(reason);
                        setStatusMessage('Connection closed by server');
                        setStatusType('warning');
                    } else {
                        // Abnormal closure
                        const reason = event.reason || `Connection closed unexpectedly (code: ${event.code})`;
                        setConnectionError(reason);
                        setStatusMessage('Connection lost');
                        setStatusType('error');
                    }
                }
            };

            setWebSocket(ws);
            webSocketRef.current = ws;
            console.log('[Host] WebSocket initialization complete, waiting for onopen event...');

            // Wait for the WebSocket to actually connect before returning
            // This keeps "Starting Session..." visible until connected
            await connectionPromise;
            return true;

        } catch (error) {
            console.error('[Host] WebSocket initialization failed:', {
                error: error,
                message: error.message,
                stack: error.stack,
                sessionId: sessionId,
                serverUrl: serverUrl,
                timestamp: new Date().toISOString()
            });
            addLog(`WebSocket init failed: ${error.message}`, 'error');
            trackException(error, {
                sessionId,
                errorType: 'TranslationHost_WebSocketInitFailed',
                serverUrl
            });
            return false;
        }
    };

    // Start Session
    const startSession = async () => {
        if (isSessionActive || isStartingSession) return;

        setIsStartingSession(true);
        setStatusMessage('Initializing session...');
        setStatusType('info');
        addLog('Starting session...');

        // Initialize distributed tracing for this session
        const traceContext = startSessionTrace(sessionId);
        trackEvent('TranslationHost_SessionStarting', {
            sessionId,
            sourceLanguage,
            targetLanguages: targetLanguages.join(','),
            transcriber,
            translator,
            model: getFinalModel(),
            engineVersion
        });
        console.log('[Host] Distributed tracing initialized:', traceContext.traceId);

        // Reset last recorded time

        try {
            // 1. Verify JWT token is available
            if (!getJwtToken()) {
                throw new Error('JWT token not available. Please log in again.');
            }
            console.log('[Host] JWT token available for authentication');

            // 2. Create session through Azure Functions (uses JWT authentication)
            const azureFunctionUrl = functionsBaseUrl;
            const createSessionUrl = `${azureFunctionUrl}/translation/create-session?sessionId=${encodeURIComponent(sessionId)}`;

            console.log('[Host] Creating session:', {
                url: createSessionUrl,
                sessionId: sessionId,
                hasPassword: !!password,
                hasToken: !!getJwtToken()
            });

            const createResponse = await fetchWithAuth(createSessionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId: sessionId,
                    password: password || '', // Include password if provided
                    // PIM (Translate Around Me) simulation: tag the session the same
                    // way the iOS QolSync AI client does so server-side path
                    // (MeetingTranscriptInitializer / Recents / TranslationPipeline
                    // tier caps) treats it as PIM.
                    ...(simulatePimMode
                        ? { productLine: 'personal', mode: 'personal', productType: '' }
                        : {}),
                    // SuperAdmin plan-simulation override. Only sent when PIM
                    // simulation is on AND a non-default plan was chosen. Backend
                    // ignores it on non-bypass sessions, so it's safe to send.
                    ...(simulatePimMode && simulatedPlanId && simulatedPlanId !== 'auto'
                        ? { simulatedPlanId }
                        : {})
                })
            });

            console.log('[Host] Create session response:', {
                ok: createResponse.ok,
                status: createResponse.status,
                statusText: createResponse.statusText
            });

            if (!createResponse.ok) {
                const errorText = await createResponse.text();
                console.error('[Host] Session creation failed:', {
                    status: createResponse.status,
                    error: errorText
                });
                throw new Error(`Failed to create session: ${errorText}`);
            }

            const createResult = await createResponse.json();
            console.log('[Host] Session created successfully:', createResult);

            // 3. Connect SignalR (uses anonymous negotiate endpoint)
            // Subscribe to the first target language so we join the correct group
            // Host mode will receive all translations regardless of specific language
            await signalRService.connect(sessionId, targetLanguages[0], {
                isHostMode: true,
                onTranslationReceived: (text, tag, sourceText, isDegraded, durationMs, speaker) => {
                    console.log('[Host] Rx Translation:', { text: text?.substring(0, 20), speaker, durationMs, tag });

                    // Default to S1 if speaker is missing
                    const finalSpeaker = speaker || 'S1';

                    // Update degraded state if SignalR indicates issues
                    if (isDegraded && !isSignalRDegraded) {
                        setIsSignalRDegraded(true);
                        console.log('[Host] SignalR entered degraded mode - live preview disabled on server');
                    }

                    if (canUnifyModel) {
                        setUnifiedTranslations(prev => {
                            const newTranslations = [...prev];
                            if (newTranslations.length > 0 && !newTranslations[newTranslations.length - 1].isFinal) {
                                newTranslations[newTranslations.length - 1] = { text, tag, timestamp: new Date(), isFinal: true, sourceText, durationMs, speaker: finalSpeaker };
                            } else {
                                newTranslations.push({ text, tag, timestamp: new Date(), isFinal: true, sourceText, durationMs, speaker: finalSpeaker });
                            }
                            return newTranslations;
                        });
                    } else {
                        setTranslations(prev => [...prev, { text, tag, timestamp: new Date(), isFinal: true, sourceText, durationMs, speaker: finalSpeaker }]);
                        setCurrentPartial('');
                    }
                },
                onPartialTextReceived: (text, tag, durationMs, speaker) => {
                    // Default to S1 if speaker is missing
                    const finalSpeaker = speaker || 'S1';

                    if (canUnifyModel) {
                        setUnifiedTranslations(prev => {
                            const newTranslations = [...prev];
                            if (newTranslations.length > 0 && !newTranslations[newTranslations.length - 1].isFinal) {
                                newTranslations[newTranslations.length - 1] = { text, tag: tag || 'partial', timestamp: new Date(), isFinal: false, durationMs, speaker: finalSpeaker };
                            } else {
                                newTranslations.push({ text, tag: tag || 'partial', timestamp: new Date(), isFinal: false, durationMs, speaker: finalSpeaker });
                            }
                            return newTranslations;
                        });
                    } else if (enablePartialText) {
                        setCurrentPartial(text);
                    }
                },
                onSpeechActivity: () => {},
                onConnectionStateChanged: (connected, message) => {
                    console.log('[Host] SignalR connection state changed:', { connected, message });
                    setSignalRConnected(connected);
                    if (!connected) {
                        if (isSessionActive) {
                            setStatusMessage('SignalR disconnected - attempting reconnect...');
                            setStatusType('warning');
                        }
                    }
                },
                onReconnecting: (error) => {
                    console.log('[Host] SignalR reconnecting:', error);
                    setStatusMessage('Reconnecting to SignalR...');
                    setStatusType('warning');
                },
                onReconnected: () => {
                    console.log('[Host] SignalR reconnected successfully');
                    setSignalRConnected(true);
                    setStatusMessage('Broadcasting live...');
                    setStatusType('success');
                },
                onDisconnected: (error) => {
                    console.log('[Host] SignalR disconnected:', error);
                    addLog(`SignalR Disconnected: ${error?.message || 'Unknown error'}`, 'error');
                    setSignalRConnected(false);
                    if (isSessionActive) {
                        setStatusMessage('SignalR connection lost');
                        setStatusType('error');
                    }
                },
                onClose: (error) => {
                    console.log('[Host] SignalR connection closed:', error);
                    addLog(`SignalR Closed: ${error?.message || 'Unknown'}`, 'warning');
                    setSignalRConnected(false);
                },
                onError: (error) => {
                    console.error('[Host] SignalR error:', error);
                    addLog(`SignalR Error: ${error?.message}`, 'error');
                    setStatusMessage(`SignalR error: ${error?.message || 'Unknown error'}`);
                    setStatusType('error');
                }
            });
            setSignalRConnected(true);

            // 4. Initialize Audio
            const audioInitialized = await initializeAudio();
            if (!audioInitialized) {
                setIsStartingSession(false);
                stopSession();
                return;
            }

            // 5. Initialize WebSocket
            const wsInitialized = await initializeWebSocket();
            if (!wsInitialized) {
                setIsStartingSession(false);
                stopSession();
                return;
            }

            setIsSessionActive(true);
            // Clear existing translations to start fresh (moved from stopSession)
            setTranslations([]);
            setUnifiedTranslations([]);
            setIsSidebarOpen(false); // Auto-collapse sidebar on start
            setIsStartingSession(false);

            // Track session start time for ALL users (needed for usage tracking)
            setSessionStartTime(Date.now());

        } catch (error) {
            console.error('Start session failed:', error);
            addLog(`Start session failed: ${error.message}`, 'error');
            trackException(error, {
                sessionId,
                errorType: 'TranslationHost_SessionStartFailed',
                transcriber,
                translator
            });
            setStatusMessage(`Failed to start: ${error.message}`);
            setStatusType('error');
            setIsStartingSession(false);
            stopSession();
        }
    };

    // Stop Session
    const stopSession = useCallback(async () => {



        console.log('[Host] Stopping session:', {
            sessionId,
            webSocketState: webSocketRef.current?.readyState,
            signalRConnected,
            timestamp: new Date().toISOString()
        });

        setIsStoppingSession(true);
        isRecordingRef.current = false;

        // Clear any pending reconnection attempts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        reconnectAttemptsRef.current = 0;

        try {
            // STEP 1: Call the Azure Function to stop the session (uses JWT authentication)
            // This will broadcast SessionCompleted/SessionEnded events to all participants BEFORE cleanup
            try {
                const functionUrl = functionsBaseUrl;

                console.log('[Host] Calling stop-session Azure Function to notify participants...');
                const stopUrl = `${functionUrl}/translation/stop-session?sessionId=${encodeURIComponent(sessionId)}`;

                // Call stop-session - the backend will broadcast to participants before cleanup.
                // A 5xx, a dropped request or one unanswered for 8 s is retried (3 attempts, #4907) so the room
                // closes now instead of waiting ~10 minutes for the orphan-room reconcile.
                const stopResponse = await retryStopSession((signal) => fetchWithAuth(stopUrl, {
                    method: 'POST',
                    signal
                }));

                console.log('[Host] Stop-session response:', {
                    ok: stopResponse.ok,
                    status: stopResponse.status,
                    sessionId
                });

                if (!stopResponse.ok) {
                    console.warn('[Host] Failed to properly stop session:', stopResponse.status);
                }
            } catch (error) {
                console.error('[Host] Failed to call stop-session:', error);
            }

            // STEP 2: Clean up WebSocket and audio resources
            if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
                try {
                    webSocketRef.current.send(JSON.stringify({ type: 'session_end' }));
                } catch (error) {
                    console.warn('Failed to send session end signal:', error);
                }
            }

            // STEP 3: Clean up audio resources
            if (processorNode) processorNode.disconnect();
            if (sourceNode) sourceNode.disconnect();
            if (audioContext) await audioContext.close();
            if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());

            // STEP 4: Close WebSocket connection
            if (webSocketRef.current) {
                webSocketRef.current.close();
            }

            // STEP 5: Disconnect SignalR (intentional=true for manual disconnect)
            await signalRService.disconnect(true);

            // STEP 7: Update UI state
            // Session has been stopped and participants have been notified

            setMediaStream(null);
            setAudioContext(null);
            setProcessorNode(null);
            setSourceNode(null);
            setWebSocket(null);
            webSocketRef.current = null;
            setSignalRConnected(false);
            setIsSessionActive(false);
            setIsSessionPaused(false);
            setIsConnected(false);
            setHasEverConnected(false); // Reset for next session
            // Do NOT clear translations here - allow user to download them
            // setTranslations([]);
            // setUnifiedTranslations([]);
            setCurrentPartial('');
            setSessionStartTime(null);
            setStatusMessage('Session ended');
            setStatusType('info');

            // Track session ended event and clear trace context
            const traceId = getCurrentTraceId();
            trackEvent('TranslationHost_SessionEnded', {
                sessionId,
                traceId,
                durationSeconds: sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0
            });
            clearTraceContext();
            console.log('[Host] Distributed tracing cleared for session:', traceId);
        } finally {
            setIsStoppingSession(false);
        }
    }, [processorNode, sourceNode, audioContext, mediaStream, sessionId, signalRConnected, sessionStartTime, functionsBaseUrl]);

    // Toggle Pause/Resume (UI Button)
    const togglePause = async () => {
        if (isSessionPaused) {
            await callResumeSession();
        } else {
            await callPauseSession();
        }
    };

    return (
        <div className="host-dashboard">
            {!functionsTargetResolved && (
                <div
                    role="alert"
                    className="error-message"
                    style={{ padding: '8px 12px', fontSize: '0.875rem', color: 'var(--error, #dc2626)' }}
                >
                    Backend target was not resolved from this deployment's configuration. Falling back to{' '}
                    <code>{functionsBaseUrl}</code>. Sessions started here are created on that backend.
                </div>
            )}
            {/* Sidebar */}
            <div className={`host-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <div className="sidebar-brand">
                        <img src="/assets/qolsync-logo.svg" alt="QolSync AI" className="brand-logo" />
                        <h2 className="brand-title qolsync-brand-text"><span className="brand-qol">Qol</span><span className="brand-sync">Sync</span><span className="brand-ai">AI</span></h2>
                    </div>
                    <div className="flex gap-2">

                        <Button variant="ghost" size="sm" onClick={() => setIsSidebarOpen(false)}>
                            <X size={20} />
                        </Button>
                    </div>
                </div>

                <div className="sidebar-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
                        onClick={() => setActiveTab('general')}
                    >
                        General
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'advanced' ? 'active' : ''}`}
                        onClick={() => setActiveTab('advanced')}
                    >
                        Advanced
                    </button>
                    {/* Only show glossary management if user has a tenant or is Platform Admin */}
                    {(user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin' || user?.TenantId) && (
                        <button
                            className="tab-btn tab-btn-icon"
                            onClick={() => navigate('/glossary')}
                            title="Manage Glossaries"
                        >
                            <Book size={16} />
                        </button>
                    )}
                    {canReadPlatformAdmin(user) && (
                        <button
                            className="tab-btn tab-btn-icon"
                            onClick={() => navigate('/admin-settings')}
                            title="Admin Settings"
                        >
                            <Settings size={16} />
                        </button>
                    )}
                </div>

                <div className="sidebar-content">
                    {activeTab === 'general' && (
                        <div className="settings-group">
                            {/* Tenant Selection */}
                            <div className="form-group">
                                <label className="ui-input-label">Tenant</label>
                                {canSelectTenant ? (
                                    <div className="select-wrapper">
                                        <select
                                            className="ui-select"
                                            value={selectedTenantId}
                                            onChange={(e) => {
                                                setSelectedTenantId(e.target.value);
                                                const t = tenants.find(t => t.RowKey === e.target.value);
                                                setTenantName(t ? t.TenantName : '');
                                            }}
                                            disabled={isSessionActive}
                                        >
                                            <option value="">Select Tenant...</option>
                                            {tenants.map(t => (
                                                <option key={t.RowKey} value={t.RowKey}>{t.TenantName}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <Input
                                        value={tenantName || 'No Tenant Assigned'}
                                        disabled={true}
                                    />
                                )}
                            </div>

                            {/* Category Glossary Selection - Always shown */}
                            <div className="form-group">
                                <label className="ui-input-label">Category Glossary (Optional)</label>
                                <div className="select-wrapper">
                                    <select
                                        className="ui-select"
                                        value={selectedSharedGlossary}
                                        onChange={(e) => setSelectedSharedGlossary(e.target.value)}
                                        disabled={isSessionActive}
                                    >
                                        <option value="">None</option>
                                        {sharedGlossaries.map(g => (
                                            <option key={g.sharedGlossaryName || g.categoryKey || g.name} value={g.sharedGlossaryName || g.categoryKey || g.name}>
                                                {getGlossaryDisplayName(g)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Tenant Glossary Selection - Only shown when host has a tenant */}
                            {selectedTenantId && (
                                <div className="form-group">
                                    <label className="ui-input-label">Tenant Glossary (Optional)</label>
                                    <div className="select-wrapper">
                                        <select
                                            className="ui-select"
                                            value={selectedGlossary}
                                            onChange={(e) => setSelectedGlossary(e.target.value)}
                                            disabled={isSessionActive}
                                        >
                                            <option value="">None</option>
                                            {tenantGlossaries.map(g => (
                                                <option key={g.tenantGlossaryName} value={g.tenantGlossaryName}>
                                                    {getGlossaryDisplayName(g)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <Input
                                label="Session ID"
                                value={sessionId}
                                onChange={(e) => setSessionId(e.target.value)}
                                disabled={isSessionActive}
                            />

                            <div className="form-group">
                                <label className="ui-input-label">Source Language</label>
                                <LanguageSelector
                                    selectedLanguages={sourceLanguage ? [sourceLanguage] : []}
                                    onSelectionChange={(codes) => setSourceLanguage(codes[0] || '')}
                                    singleSelect={true}
                                    disabled={isSessionActive}
                                    placeholder="Select source language"
                                />
                            </div>

                            <div className="form-group">
                                <label className="ui-input-label">Target Languages</label>
                                <LanguageSelector
                                    selectedLanguages={targetLanguages}
                                    onSelectionChange={(codes) => setTargetLanguages(codes)}
                                    singleSelect={false}
                                    disabled={isSessionActive}
                                    placeholder="Select target languages"
                                />
                                {targetLanguages.length === 0 && (
                                    <div className="error-message" style={{ fontSize: '0.875rem', marginTop: '8px', color: 'var(--error)' }}>
                                        Target language is required
                                    </div>
                                )}
                            </div>

                            <Input
                                label="Session Password (Optional)"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isSessionActive}
                                icon={<Lock size={16} />}
                            />
                        </div>
                    )}

                    {activeTab === 'advanced' && (
                        <div className="settings-group">
                            {/* PIM Simulation — at the top so it's the first thing the tester sees */}
                            <div className="settings-subsection">
                                <h3 className="subsection-title">Test Modes</h3>
                                <label className="toggle-row">
                                    <span>Simulate PIM (iOS "Translate Around Me")</span>
                                    <input
                                        type="checkbox"
                                        checked={simulatePimMode}
                                        onChange={(e) => setSimulatePimMode(e.target.checked)}
                                        disabled={isSessionActive}
                                    />
                                </label>
                                <small className="form-help-text">
                                    Tags the session as PIM (productLine="personal", mode="personal") so server-side flows (Recents library, MeetingTranscriptInitializer, V2 tier caps) treat it the same as an iOS QolSync AI client. Existing defaults (TTS off, partial-text "original") already match PIM client behaviour. Use this to reproduce iOS PIM bugs from the AdminPortal without an iOS device.
                                </small>

                                {/* SuperAdmin-only: simulate as if the host were on a specific plan.
                                    Only meaningful while Simulate PIM is on (PIM is the flow being
                                    tested). Backend honors this only when HasBillingBypass=true. */}
                                {simulatePimMode && (user?.IsPlatformAdmin || user?.Role === 'SuperAdmin') && (
                                    <div className="form-group" style={{ marginTop: '12px' }}>
                                        <label className="ui-input-label">Simulate as Plan</label>
                                        <select
                                            className="ui-select"
                                            value={simulatedPlanId}
                                            onChange={(e) => setSimulatedPlanId(e.target.value)}
                                            disabled={isSessionActive}
                                        >
                                            <option value="auto">Auto — current user's plan (bypass = Unlimited)</option>
                                            <option value="plan_free_v2">Free</option>
                                            <option value="plan_meeting_v2">Meeting</option>
                                            <option value="plan_meeting_pro_v2">Meeting Pro</option>
                                            <option value="plan_webinar_v2">Webinar</option>
                                        </select>
                                        <small className="form-help-text">
                                            Forces GetSessionPlan to return the chosen plan's features and limits, letting you reproduce the constraints a real Free / Meeting / Meeting Pro / Webinar user would hit (session duration cap, Q&A gates, max participants, etc.). SuperAdmin bypass is required — backend silently ignores this on non-bypass sessions.
                                        </small>
                                    </div>
                                )}
                            </div>

                            {/* Speech Recognition Group */}
                            <div className="settings-subsection">
                                <h3 className="subsection-title">Speech Recognition (STT)</h3>
                                <div className="form-group">
                                    <label className="ui-input-label">Provider</label>
                                    <select
                                        className="ui-select"
                                        value={transcriber}
                                        onChange={(e) => handleTranscriberChange(e.target.value)}
                                        disabled={isSessionActive}
                                    >
                                        <option value="Auto">Auto (recommended — best provider per language)</option>
                                        <option value="Speechmatics">Speechmatics (English / European)</option>
                                        <option value="AssemblyAI">AssemblyAI (Universal-Streaming v3, English accent variants)</option>
                                        <option value="GptRealtimeWhisper">OpenAI gpt-realtime-whisper (Whisper Realtime — opt-in, premium cost)</option>
                                        <option value="Soniox">Soniox (realtime STT — diarization, language-ID, native translation)</option>
                                        <option value="Microsoft">Microsoft Azure Speech</option>
                                        <option value="Google">Google Cloud STT</option>
                                    </select>
                                    {transcriber === 'Auto' && (
                                        <small className="form-help-text">
                                            Routing: en-IN / en-PH / en-NG / en-ZA → AssemblyAI · ko-KR / ja-JP → Google · zh-CN / zh-SG / vi-VN / id-ID / ar-* / ms-MY / ta-IN → Soniox · yue → GPT Realtime Whisper · other en-* → Speechmatics · th-TH / zh-TW / zh-HK and default fallback → DeepGram
                                        </small>
                                    )}
                                </div>

                                {transcriber === 'Google' && (
                                    <>
                                        <div className="form-group">
                                            <label className="ui-input-label">Google STT Model</label>
                                            <select
                                                className="ui-select"
                                                value={googleSttModel}
                                                onChange={(e) => setGoogleSttModel(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="">Default</option>
                                                <option value="latest_long">Latest Long</option>
                                                <option value="latest_short">Latest Short</option>
                                                <option value="command_and_search">Command and Search</option>
                                                <option value="phone_call">Phone Call</option>
                                            </select>
                                        </div>
                                        <label className="toggle-row">
                                            <span>Single Utterance Mode</span>
                                            <input
                                                type="checkbox"
                                                checked={singleUtterance}
                                                onChange={(e) => setSingleUtterance(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                    </>
                                )}

                                {transcriber === 'GptRealtimeWhisper' && (
                                    <>
                                        <div className="form-help-text" style={{ marginTop: '8px' }}>
                                            OpenAI Realtime API (gpt-realtime-whisper, released 2026-05-07). Direct OpenAI endpoint — Azure OpenAI is not supported for this provider.
                                            <br />
                                            <strong>Cost:</strong> ~$1.02/hr (2-7× other providers). Per-session cost emitted as <code>Asr.GptRealtimeWhisper.SessionCostUsd</code>.
                                            <br />
                                            <strong>Capabilities:</strong> partials + logprob-confidence, no server-side VAD (delay-driven finals), no diarization, no prompt steering.
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Model (override)</label>
                                            <input
                                                type="text"
                                                className="ui-input"
                                                placeholder="gpt-realtime-whisper (default)"
                                                value={gptRealtimeWhisperModel}
                                                onChange={(e) => setGptRealtimeWhisperModel(e.target.value)}
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Leave blank to use the server-configured model. Only set this if OpenAI has shipped a preview variant.
                                            </small>
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Transcription Delay</label>
                                            <select
                                                className="ui-select"
                                                value={gptRealtimeWhisperDelay}
                                                onChange={(e) => setGptRealtimeWhisperDelay(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="">(server default)</option>
                                                <option value="minimal">minimal — fastest, lowest quality</option>
                                                <option value="low">low</option>
                                                <option value="medium">medium</option>
                                                <option value="high">high</option>
                                                <option value="xhigh">xhigh — slowest, highest quality</option>
                                            </select>
                                            <small className="form-help-text">
                                                OpenAI Realtime API enum. Lower = faster partial tokens; higher = better transcript quality. Server default is used when "(server default)" is selected.
                                            </small>
                                        </div>

                                        <label className="toggle-row" style={{ marginTop: '10px' }}>
                                            <span>Include Logprobs (confidence scores)</span>
                                            <input
                                                type="checkbox"
                                                checked={gptRealtimeWhisperIncludeLogprobs}
                                                onChange={(e) => setGptRealtimeWhisperIncludeLogprobs(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                        <div className="form-help-text">
                                            Requests per-token log probabilities from OpenAI, mapped to confidence telemetry. Adds slight bandwidth overhead.
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Commit on Silence (ms)</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                placeholder="500 (server default)"
                                                value={gptRealtimeWhisperCommitSilenceMs}
                                                onChange={(e) => setGptRealtimeWhisperCommitSilenceMs(e.target.value)}
                                                min="0"
                                                max="5000"
                                                step="50"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                How many ms of consecutive silence to wait before committing a segment. Lower (200-300) = faster cuts, more segments. Higher (700-1000) = more natural utterances. Leave blank for server default (500 ms). Range: 0-5000.
                                            </small>
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Max Commit Interval (ms)</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                placeholder="10000 (server default)"
                                                value={gptRealtimeWhisperCommitMaxIntervalMs}
                                                onChange={(e) => setGptRealtimeWhisperCommitMaxIntervalMs(e.target.value)}
                                                min="1000"
                                                max="60000"
                                                step="500"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Safety-net: even without silence, force a commit every N ms. Caps worst-case time-to-final on continuous speech. Lower = faster finals but more segments. Leave blank for server default (10000 ms). Range: 1000-60000.
                                            </small>
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Noise Reduction</label>
                                            <select
                                                className="ui-select"
                                                value={gptRealtimeWhisperNoiseReduction}
                                                onChange={(e) => setGptRealtimeWhisperNoiseReduction(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="">(off — server default)</option>
                                                <option value="near_field">near_field — close-talking mic (headset, handheld)</option>
                                                <option value="far_field">far_field — distant mic (laptop, conference room)</option>
                                            </select>
                                            <small className="form-help-text">
                                                OpenAI <code>input_audio_noise_reduction</code>. Filters audio before VAD/transcription — does NOT amplify the mic. <strong>near_field</strong> is tuned for close mics (removes breath/pops); <strong>far_field</strong> is tuned for distant mics (removes room noise/AC hum). Default <em>off</em>. Note: noise reduction can add latency — leave off unless you observe a quality problem.
                                            </small>
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Silence Amplitude Threshold</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                placeholder="300 (server default)"
                                                value={gptRealtimeWhisperCommitSilenceAmplitude}
                                                onChange={(e) => setGptRealtimeWhisperCommitSilenceAmplitude(e.target.value)}
                                                min="0"
                                                max="10000"
                                                step="50"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Client-side silence detector — mean absolute PCM16 sample value below which an audio chunk counts as silence (drives the commit cadence). Higher (500-1000) = more permissive, treats quiet speech as silence (good in noisy rooms). Lower (50-150) = stricter, only true silence counts (good in studio). Leave blank for server default (300 ≈ 1% of int16 max). Range: 0-10000.
                                            </small>
                                        </div>
                                    </>
                                )}

                                {transcriber === 'Soniox' && (
                                    <>
                                        <div className="form-help-text" style={{ marginTop: '8px' }}>
                                            Soniox realtime STT (<code>wss://stt-rt.soniox.com</code>). Opt-in provider — only reached when explicitly selected.
                                            <br />
                                            <strong>Capabilities:</strong> speaker diarization, per-token language identification, endpoint detection (always on), and optional in-STT translation.
                                            <br />
                                            Server defaults: diarization / language-ID <em>on</em>, max endpoint delay 2000&nbsp;ms. Leave controls at their defaults to use the server configuration.
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Translation Mode</label>
                                            <select
                                                className="ui-select"
                                                value={sonioxTranslationMode}
                                                onChange={(e) => setSonioxTranslationMode(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="">Engine Chain (server default — translate via our pipeline)</option>
                                                <option value="EngineChain">Engine Chain (Soniox transcribes; pipeline translates)</option>
                                                <option value="SonioxNative">Soniox Native (translate inside the STT connection)</option>
                                            </select>
                                            <small className="form-help-text">
                                                <strong>Engine Chain</strong> transcribes only and feeds the existing translator chain — supports any number of target languages. <strong>Soniox Native</strong> translates one target language inside the STT connection; additional targets fall back to Engine Chain.
                                            </small>
                                            {sonioxTranslationMode === 'SonioxNative' && targetLanguages.length > 1 && (
                                                <small className="form-help-text" style={{ color: 'var(--warning, #b45309)' }}>
                                                    ⚠ {targetLanguages.length} target languages selected. Soniox Native translates only the first; the rest fall back to Engine Chain.
                                                </small>
                                            )}
                                            {sonioxTranslationMode === 'SonioxNative' && enableTts && (
                                                <small className="form-help-text" style={{ color: 'var(--warning, #b45309)' }}>
                                                    ⚠ TTS is enabled, but Soniox Native broadcasts the primary target as text only — it bypasses the TTS audio path, so the primary target produces NO spoken audio. Use Engine Chain if you need spoken interpretation for the primary target.
                                                </small>
                                            )}
                                        </div>

                                        <label className="toggle-row" style={{ marginTop: '10px' }}>
                                            <span>Speaker Diarization</span>
                                            <input
                                                type="checkbox"
                                                checked={sonioxEnableSpeakerDiarization}
                                                onChange={(e) => setSonioxEnableSpeakerDiarization(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                        <div className="form-help-text">
                                            Populates the per-token <code>speaker</code> field so transcripts attribute lines to distinct speakers. On by default.
                                        </div>

                                        <label className="toggle-row" style={{ marginTop: '10px' }}>
                                            <span>Language Identification</span>
                                            <input
                                                type="checkbox"
                                                checked={sonioxEnableLanguageIdentification}
                                                onChange={(e) => setSonioxEnableLanguageIdentification(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                        <div className="form-help-text">
                                            Populates the per-token <code>language</code> field for mixed-language audio. On by default.
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Max Endpoint Delay (ms)</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                placeholder="1500 (server default)"
                                                value={sonioxMaxEndpointDelayMs}
                                                onChange={(e) => setSonioxMaxEndpointDelayMs(e.target.value)}
                                                min="500"
                                                max="3000"
                                                step="100"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                The longest silence Soniox waits after speech stops before emitting <code>&lt;end&gt;</code> and finalizing the turn. It does NOT bound a segment during continuous speech — the server's committed-prefix cut does that. Leave blank for server default (1500&nbsp;ms). Range: 500–3000.
                                            </small>
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Language Hints</label>
                                            <input
                                                type="text"
                                                className="ui-input"
                                                placeholder="e.g. en,ko,ja"
                                                value={sonioxLanguageHints}
                                                onChange={(e) => setSonioxLanguageHints(e.target.value)}
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Comma-separated ISO 639-1 codes that bias Soniox language detection toward the languages you expect. Leave blank to let Soniox auto-detect.
                                            </small>
                                        </div>
                                    </>
                                )}

                                {transcriber === 'DeepGram' && (
                                    <>
                                        <label className="toggle-row">
                                            <span>Sentence Buffering</span>
                                            <input
                                                type="checkbox"
                                                checked={useSentenceBuffering}
                                                onChange={(e) => setUseSentenceBuffering(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                        <div className="form-help-text">
                                            Waits for punctuation marks (. ! ?) before translating for better sentence quality.
                                        </div>
                                    </>
                                )}

                                {transcriber === 'Speechmatics' && (
                                    <>

                                        <label className="toggle-row">
                                            <span>Enable V2 Post-Processing</span>
                                            <input
                                                type="checkbox"
                                                checked={enableV2PostProcessing}
                                                onChange={(e) => setEnableV2PostProcessing(e.target.checked)}
                                                disabled={isSessionActive}
                                            />
                                        </label>
                                        <div className="form-help-text">
                                            Reduces over-segmentation by merging incomplete sentence fragments.
                                        </div>

                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label className="ui-input-label">Max Delay (seconds)</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                value={speechmaticsMaxDelay}
                                                onChange={(e) => setSpeechmaticsMaxDelay(parseFloat(e.target.value) || 2.0)}
                                                min="0.1"
                                                max="10.0"
                                                step="0.1"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Maximum delay for final transcriptions (higher = better quality).
                                            </small>
                                        </div>
                                    </>
                                )}

                                {transcriber === 'Microsoft' && (
                                    <>
                                        <div className="form-group">
                                            <label className="ui-input-label">Confidence Threshold: {confidenceThreshold}</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.1"
                                                value={confidenceThreshold}
                                                onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
                                                className="ui-range"
                                                disabled={isSessionActive}
                                            />
                                        </div>
                                        <div className="toggles-list">
                                            <label className="toggle-row">
                                                <span>Semantic Segmentation</span>
                                                <input
                                                    type="checkbox"
                                                    checked={useSemanticSegmentation}
                                                    onChange={(e) => {
                                                        setUseSemanticSegmentation(e.target.checked);
                                                        if (e.target.checked) {
                                                            if (interpreterMode) setInterpreterMode(false);
                                                            setSegmentationSilenceTimeout(-1);
                                                        }
                                                    }}
                                                    disabled={isSessionActive || interpreterMode}
                                                />
                                            </label>
                                            <div className="form-help-text">
                                                Enables intelligent sentence boundary detection based on context.
                                                {interpreterMode && <span className="text-warning"> Disabled in Interpreter Mode.</span>}
                                            </div>

                                            <label className="toggle-row">
                                                <span>Interpreter Mode</span>
                                                <input
                                                    type="checkbox"
                                                    checked={interpreterMode}
                                                    onChange={(e) => {
                                                        setInterpreterMode(e.target.checked);
                                                        if (e.target.checked) {
                                                            setUseSemanticSegmentation(false);
                                                            setSegmentationSilenceTimeout(250);
                                                            setLongSentenceModeSeconds(0);
                                                        } else {
                                                            setSegmentationSilenceTimeout(-1);
                                                        }
                                                    }}
                                                    disabled={isSessionActive || useSemanticSegmentation}
                                                />
                                            </label>
                                            <div className="form-help-text">
                                                Optimizes for real-time interpretation with shorter intervals.
                                                {useSemanticSegmentation && <span className="text-warning"> Disabled when Semantic Segmentation is active.</span>}
                                            </div>
                                        </div>

                                        {!useSemanticSegmentation && (
                                            <div className="form-group" style={{ marginTop: '10px' }}>
                                                <label className="ui-input-label">Segmentation Timeout (ms)</label>
                                                <input
                                                    type="number"
                                                    className="ui-input"
                                                    value={segmentationSilenceTimeout}
                                                    onChange={(e) => setSegmentationSilenceTimeout(parseInt(e.target.value) || -1)}
                                                    min="-1"
                                                    max="2000"
                                                    step="50"
                                                    disabled={isSessionActive}
                                                    placeholder="-1 (Default)"
                                                />
                                                <small className="form-help-text">
                                                    Wait time for silence before segmenting. -1 for default.
                                                </small>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Long Sentence Mode — mutually exclusive with Interpreter Mode */}
                                <div className="form-group">
                                    <label className="ui-input-label">Long Sentence Mode</label>
                                    <select
                                        className="ui-select"
                                        data-ai-id="long-sentence-mode-select"
                                        data-ai-action="change"
                                        data-ai-feature="advanced-settings"
                                        value={longSentenceModeSeconds}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            setLongSentenceModeSeconds(val);
                                            if (val > 0) setInterpreterMode(false);
                                        }}
                                        disabled={isSessionActive || interpreterMode}
                                    >
                                        <option value={0}>Off</option>
                                        <option value={10}>10 sec</option>
                                        <option value={15}>15 sec</option>
                                        <option value={20}>20 sec</option>
                                        <option value={25}>25 sec</option>
                                        <option value={30}>30 sec</option>
                                        <option value={45}>45 sec*</option>
                                        <option value={60}>60 sec*</option>
                                    </select>
                                    <small className="form-help-text">
                                        Waits for a complete sentence before translating. Increases accuracy for transcription scripts.
                                        {interpreterMode && <span className="text-warning"> Disabled in Interpreter Mode.</span>}
                                        {longSentenceModeSeconds >= 45 && (
                                            <span className="text-warning"> * For post-meeting transcription only. Live viewers will see {longSentenceModeSeconds}+ second delays.</span>
                                        )}
                                    </small>
                                </div>
                            </div>

                            <div className="settings-subsection">
                                <h3 className="subsection-title">Translation</h3>

                                {/* V2 Engine Version Selection */}
                                <div className="form-group">
                                    <label className="ui-input-label">Engine Version</label>
                                    <select
                                        className="ui-select"
                                        value={engineVersion}
                                        onChange={(e) => setEngineVersion(e.target.value)}
                                        disabled={isSessionActive}
                                    >
                                        <option value="v1">V1 - Standard</option>
                                        <option value="v2">V2 - Interpretive (Beta)</option>
                                    </select>
                                    <small className="form-help-text">
                                        V2 uses interpretive translation with parallel model racing for faster, more natural results.
                                    </small>
                                </div>

                                {/* V2-specific settings */}
                                {engineVersion === 'v2' && (
                                    <>
                                        <div className="form-group">
                                            <label className="ui-input-label">Primary Model</label>
                                            <select
                                                className="ui-select"
                                                value={v2PrimaryModel}
                                                onChange={(e) => setV2PrimaryModel(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="Auto">Auto (recommended — server picks from FinalModelChains)</option>
                                                <optgroup label="OpenAI — GPT-5.5 (latest flagship)">
                                                    <option value="gpt-5.5">GPT-5.5 🆕 (latest flagship)</option>
                                                </optgroup>
                                                <optgroup label="OpenAI — GPT-5.4 family">
                                                    <option value="gpt-5.4">GPT-5.4 ⭐ Default</option>
                                                    <option value="gpt-5.4-mini">GPT-5.4 Mini 🆕</option>
                                                    <option value="gpt-5.4-nano">GPT-5.4 Nano 🆕</option>
                                                </optgroup>
                                                <optgroup label="OpenAI — GPT-5 base + smaller">
                                                    <option value="gpt-5">GPT-5 🆕</option>
                                                    <option value="gpt-5-mini">GPT-5 Mini</option>
                                                    <option value="gpt-6-luna">GPT-6 Luna</option>
                                                    <option value="gpt-5.6-luna">GPT-5.6 Luna</option>
                                                </optgroup>
                                                <optgroup label="OpenAI — GPT-4.1 family">
                                                    <option value="gpt-4.1">GPT-4.1</option>
                                                    <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                                                </optgroup>
                                                <optgroup label="OpenAI — Legacy (deprecated)">
                                                    <option value="gpt-5.2">GPT-5.2 (demotes to 5.4)</option>
                                                    <option value="gpt-5.1">GPT-5.1</option>
                                                </optgroup>
                                                <optgroup label="Google — Gemini 3.x">
                                                    <option value="gemini-3.1-flash">Gemini 3.1 Flash 🆕</option>
                                                    <option value="gemini-3.1-flash-lite">Gemini 3.1 Flash Lite 🆕</option>
                                                    <option value="gemini-3-flash">Gemini 3 Flash 🆕</option>
                                                </optgroup>
                                                <optgroup label="Google — Gemini 2.5">
                                                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                                                    <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite ⚡</option>
                                                </optgroup>
                                            </select>
                                            <small className="form-help-text">
                                                V2 races this model with the demotion chain from <code>V2PipelineModels.ModelDemotions</code> (e.g., gpt-5.4 → gpt-5.4-mini → gpt-5.4-nano → gpt-5-mini). First response wins. Pro tier allows nano/mini/5.4; other selections clamp to tier default. Auto lets the server pick from its tuned <code>FinalModelChains</code>.
                                            </small>
                                        </div>
                                        {/* Reasoning Effort — only meaningful for GPT-5.x reasoning models
                                            (Auto resolves to gpt-5.4; -mini/-nano and Gemini are not reasoning models). */}
                                        {(v2PrimaryModel === 'Auto' ||
                                          (v2PrimaryModel.startsWith('gpt-5') &&
                                           !v2PrimaryModel.includes('-mini') &&
                                           !v2PrimaryModel.includes('-nano'))) && (
                                            <div className="form-group">
                                                <label className="ui-input-label">Reasoning Effort</label>
                                                <select
                                                    className="ui-select"
                                                    value={reasoningEffort}
                                                    onChange={(e) => setReasoningEffort(e.target.value)}
                                                    disabled={isSessionActive}
                                                >
                                                    <option value="default">Default (server config)</option>
                                                    <option value="minimal">minimal — fastest, lowest quality</option>
                                                    <option value="low">low</option>
                                                    <option value="medium">medium</option>
                                                    <option value="high">high — slowest, highest quality</option>
                                                </select>
                                                <small className="form-help-text">
                                                    OpenAI <code>reasoning_effort</code> for gpt-5.x finals. Higher effort improves translation quality but increases latency and token cost (medium ≈ 2× the per-call cost of low). "Default" sends no override — the server uses <code>ModelRouting:ReasoningEffort</code>.
                                                </small>
                                            </div>
                                        )}
                                        <div className="form-group">
                                            <label className="ui-input-label">Primary Timeout (seconds)</label>
                                            <input
                                                type="number"
                                                className="ui-input"
                                                value={v2PrimaryTimeout}
                                                onChange={(e) => setV2PrimaryTimeout(e.target.value)}
                                                min="0.1"
                                                max="60"
                                                step="0.1"
                                                disabled={isSessionActive}
                                            />
                                            <small className="form-help-text">
                                                Wait this long for primary model before using fallback (1-30s).
                                            </small>
                                        </div>
                                        <div className="form-group">
                                            <label className="ui-input-label">Rolling Summary (per-session override)</label>
                                            <select
                                                className="ui-select"
                                                value={rollingSummaryOverride}
                                                onChange={(e) => setRollingSummaryOverride(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="default">Default (use server config)</option>
                                                <option value="on">Force enabled (hybrid context: summary + recent raw)</option>
                                                <option value="off">Force disabled (full raw history)</option>
                                            </select>
                                            <small className="form-help-text">
                                                Compresses prior history into a rolling AI summary (gpt-4o-mini) to reduce per-call input tokens. "Default" defers to the server&apos;s appsettings flag; "Force" values override for THIS session only — useful for A/B testing without restarting the TranslationEngine.
                                            </small>
                                        </div>
                                        <div className="form-group">
                                            <label className="ui-input-label">Session Scenario</label>
                                            <select
                                                className="ui-select"
                                                value={sessionType}
                                                onChange={(e) => setSessionType(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                <option value="Conversation">Conversation (default — no preset)</option>
                                                <option value="Lecture">Lecture (single primary speaker, brief Q&amp;A)</option>
                                                <option value="Discussion">Discussion (multiple speakers, names addressed)</option>
                                                <option value="Symposium">Symposium (formal, long speaker turns)</option>
                                                <option value="News">News (anchor + reporter, scripted handoffs)</option>
                                            </select>
                                            <small className="form-help-text">
                                                Injects a scenario-specific preset into the system prompt and biases the rolling summary template. "Conversation" is the default behavior (no preset injected).
                                            </small>
                                        </div>
                                    </>
                                )}

                                {engineVersion !== 'v2' && (
                                    <>
                                        <div className="form-group">
                                            <label className="ui-input-label">Translator</label>
                                            <select
                                                className="ui-select"
                                                value={translator}
                                                onChange={(e) => handleTranslatorChange(e.target.value)}
                                                disabled={isSessionActive}
                                            >
                                                {Object.keys(translatorModels).map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>

                                        {translatorModels[translator]?.length > 0 && (
                                            <div className="form-group">
                                                <label className="ui-input-label">Model</label>
                                                <select
                                                    className="ui-select"
                                                    value={model}
                                                    onChange={(e) => setModel(e.target.value)}
                                                    disabled={isSessionActive}
                                                >
                                                    <option value="Auto">Auto (recommended — server picks from config)</option>
                                                    {translatorModels[translator].map(m => (
                                                        <option key={m.value} value={m.value}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {model === 'custom' && (
                                            <Input
                                                label="Custom Model Name"
                                                value={customModelInput}
                                                onChange={(e) => setCustomModelInput(e.target.value)}
                                                disabled={isSessionActive}
                                                placeholder="e.g., gpt-4-turbo-preview"
                                            />
                                        )}
                                    </>
                                )}

                                <div className="form-group">
                                    <label className="ui-input-label">Host Instructions</label>
                                    <textarea
                                        className="ui-textarea"
                                        value={hostInstructions}
                                        onChange={(e) => setHostInstructions(e.target.value)}
                                        placeholder="Context for better translation..."
                                        disabled={isSessionActive}
                                        rows={3}
                                    />
                                </div>

                                <div className="toggles-list">
                                    <label className="toggle-row">
                                        <span>Text-to-Speech</span>
                                        <input
                                            type="checkbox"
                                            checked={enableTts}
                                            onChange={(e) => setEnableTts(e.target.checked)}
                                            disabled={isSessionActive}
                                        />
                                    </label>
                                    <label className="toggle-row">
                                        <span>Partial Results</span>
                                        <input
                                            type="checkbox"
                                            checked={enablePartialText}
                                            onChange={(e) => setEnablePartialText(e.target.checked)}
                                            disabled={isSessionActive}
                                        />
                                    </label>
                                    {enablePartialText && (
                                        <div className="form-group" style={{ marginLeft: '16px', marginTop: '4px' }}>
                                            <label className="toggle-row" style={{ cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="partialTextMode"
                                                    value="original"
                                                    checked={partialTextMode === 'original'}
                                                    onChange={() => setPartialTextMode('original')}
                                                    disabled={isSessionActive}
                                                />
                                                <span>Show Original Text</span>
                                            </label>
                                            <label className="toggle-row" style={{ cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="partialTextMode"
                                                    value="translated"
                                                    checked={partialTextMode === 'translated'}
                                                    onChange={() => setPartialTextMode('translated')}
                                                    disabled={isSessionActive}
                                                />
                                                <span>Show Translated Text</span>
                                            </label>
                                        </div>
                                    )}
                                    <label className="toggle-row">
                                        <span>Show Original</span>
                                        <input
                                            type="checkbox"
                                            checked={showSourceText}
                                            onChange={(e) => {
                                                setShowSourceText(e.target.checked);
                                                localStorage.setItem('showSourceText', e.target.checked);
                                            }}
                                        />
                                    </label>
                                    <label className="toggle-row">
                                        <span>High-Quality Translation Mode</span>
                                        <input
                                            type="checkbox"
                                            checked={canUnifyModel}
                                            onChange={(e) => setCanUnifyModel(e.target.checked)}
                                            disabled={isSessionActive}
                                        />
                                    </label>
                                </div>
                                <div className="form-help-text" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                                    Uses the same premium AI model for both live preview and final translations, ensuring consistent high-quality results.
                                    When disabled, uses faster models for live preview to reduce latency and costs, with premium models only for final translations.
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                <div className="sidebar-footer">
                    {!isSessionActive || !isConnected ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Button
                                variant="primary"
                                className="w-full"
                                onClick={isSessionActive && !isConnected ? stopSession : startSession}
                                icon={isSessionActive && !isConnected ? <AlertCircle size={18} /> : <Play size={18} />}
                                isLoading={isStartingSession || (isSessionActive && !isConnected && isStoppingSession)}
                                disabled={isStartingSession || (isSessionActive && !isConnected && isStoppingSession) || (!isSessionActive && targetLanguages.length === 0)}
                            >
                                {isSessionActive && !isConnected
                                    ? (isStoppingSession ? 'Resetting...' : 'Reset Session')
                                    : (isStartingSession ? 'Starting...' : 'Start Session')
                                }
                            </Button>
                            <div className="download-dropdown" style={{ position: 'relative' }}>
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setShowDownloadMenu(prev => !prev)}
                                    icon={<DownloadIcon size={18} />}
                                    disabled={translations.length === 0 && unifiedTranslations.length === 0}
                                >
                                    Download Transcript
                                </Button>
                                {showDownloadMenu && (
                                    <div
                                        className="download-menu"
                                        style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: 0,
                                            right: 0,
                                            marginBottom: '4px',
                                            background: 'var(--bg-surface)',
                                            border: '1px solid var(--border-default)',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            zIndex: 100,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}
                                            onClick={() => { downloadTranscript(); setShowDownloadMenu(false); }}
                                        >
                                            CSV (source + translated)
                                        </button>
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                            onClick={() => { downloadPlainText('source'); setShowDownloadMenu(false); }}
                                        >
                                            Plain Text — Source Language
                                        </button>
                                        {targetLanguages.map(lang => (
                                            <button
                                                key={lang}
                                                className="download-menu-item"
                                                style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                                onClick={() => { downloadPlainText(lang); setShowDownloadMenu(false); }}
                                            >
                                                Plain Text — {lang}
                                            </button>
                                        ))}
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                            onClick={() => { downloadPlainText('source', 'rawtext'); setShowDownloadMenu(false); }}
                                        >
                                            Raw Text — Source (no speaker)
                                        </button>
                                        {targetLanguages.map(lang => (
                                            <button
                                                key={`raw-${lang}`}
                                                className="download-menu-item"
                                                style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                                onClick={() => { downloadPlainText(lang, 'rawtext'); setShowDownloadMenu(false); }}
                                            >
                                                Raw Text — {lang} (no speaker)
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="active-controls">
                            <Button
                                variant={isSessionPaused ? "primary" : "secondary"}
                                className="w-full"
                                onClick={togglePause}
                                icon={isSessionPaused ? <Play size={18} /> : <Pause size={18} />}
                            >
                                {isSessionPaused ? "Resume" : "Pause"}
                            </Button>
                            <div className="download-dropdown" style={{ position: 'relative' }}>
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setShowDownloadMenu(prev => !prev)}
                                    icon={<DownloadIcon size={18} />}
                                    disabled={translations.length === 0 && unifiedTranslations.length === 0}
                                >
                                    Download
                                </Button>
                                {showDownloadMenu && (
                                    <div
                                        className="download-menu"
                                        style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: 0,
                                            right: 0,
                                            marginBottom: '4px',
                                            background: 'var(--bg-surface)',
                                            border: '1px solid var(--border-default)',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            zIndex: 100,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}
                                            onClick={() => { downloadTranscript(); setShowDownloadMenu(false); }}
                                        >
                                            CSV (source + translated)
                                        </button>
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                            onClick={() => { downloadPlainText('source'); setShowDownloadMenu(false); }}
                                        >
                                            Plain Text — Source Language
                                        </button>
                                        {targetLanguages.map(lang => (
                                            <button
                                                key={lang}
                                                className="download-menu-item"
                                                style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                                onClick={() => { downloadPlainText(lang); setShowDownloadMenu(false); }}
                                            >
                                                Plain Text — {lang}
                                            </button>
                                        ))}
                                        <button
                                            className="download-menu-item"
                                            style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                            onClick={() => { downloadPlainText('source', 'rawtext'); setShowDownloadMenu(false); }}
                                        >
                                            Raw Text — Source (no speaker)
                                        </button>
                                        {targetLanguages.map(lang => (
                                            <button
                                                key={`raw-${lang}`}
                                                className="download-menu-item"
                                                style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', borderTop: '1px solid var(--border-subtle)' }}
                                                onClick={() => { downloadPlainText(lang, 'rawtext'); setShowDownloadMenu(false); }}
                                            >
                                                Raw Text — {lang} (no speaker)
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Button
                                variant="danger"
                                className="w-full"
                                onClick={stopSession}
                                icon={<Square size={18} />}
                                isLoading={isStoppingSession}
                                disabled={isStoppingSession}
                            >
                                {isStoppingSession ? "Stopping..." : "Stop"}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="host-main">
                {/* Top Bar */}
                <div className="host-topbar glass">
                    <div className="topbar-left">
                        {!isSidebarOpen && (
                            <Button variant="ghost" size="sm" onClick={() => setIsSidebarOpen(true)}>
                                <Settings size={20} />
                            </Button>
                        )}
                        <div className="session-info" style={{ whiteSpace: 'nowrap' }}>
                            <span className="label">Session ID:</span>
                            <span className="value">{sessionId}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                icon={isCopied ? <Check size={14} color="#10B981" /> : <Copy size={14} />}
                                onClick={() => {
                                    const baseUrl = window.location.origin;
                                    // A room offering more than one target language must not pin every
                                    // listener to whichever one happens to be targetLanguages[0] — that
                                    // silently skips the language picker they should see. Only a
                                    // single-language room has nothing to pick, so the link may say so.
                                    const targetLanguageParam = targetLanguages.length === 1
                                        ? `&targetLanguage=${encodeURIComponent(targetLanguages[0])}`
                                        : '';
                                    const meetingLink = `${baseUrl}/receiver?sessionId=${encodeURIComponent(sessionId)}${targetLanguageParam}&enableTts=${enableTts}&showPartial=${enablePartialText}&autojoin=true`;
                                    navigator.clipboard.writeText(meetingLink);
                                    setIsCopied(true);
                                    setTimeout(() => setIsCopied(false), 2000);
                                }}
                            />
                            {sessionId && hasEverConnected && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={isAiSummaryCopied ? <Check size={14} color="#10B981" /> : <Bot size={14} color="currentColor" />}
                                    title="Copy AI Meeting link"
                                    onClick={() => {
                                        const externalBase =
                                            process.env.REACT_APP_EXTERNAL_PARTICIPANT_URL || 'https://app-test.qolsync.ai';
                                        const link = `${externalBase}/external?sessionId=${encodeURIComponent(sessionId)}&activeTab=ai`;
                                        navigator.clipboard.writeText(link);
                                        setIsAiSummaryCopied(true);
                                        setTimeout(() => setIsAiSummaryCopied(false), 2000);
                                    }}
                                >
                                    {isAiSummaryCopied ? 'Copied!' : 'AI Meeting'}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="topbar-center">
                        {isSessionActive && (
                            <>
                                <div className="live-indicator">
                                    <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'} ${isSessionPaused ? 'paused' : ''}`} />
                                    <span>{isSessionPaused ? 'PAUSED' : (isConnected ? 'LIVE' : 'OFFLINE')}</span>
                                </div>
                                {isSignalRDegraded && (
                                    <div className="degraded-indicator" title="Live preview disabled due to network conditions">
                                        <AlertCircle size={14} />
                                        <span>Preview Off</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="topbar-right">
                        {isSessionActive && (
                            <>
                                <div className="audio-meter">
                                    <Mic size={16} className={audioLevel > 0.1 ? 'active' : ''} />
                                    <div className="meter-bar">
                                        <div className="meter-fill" style={{ width: `${audioLevel * 100}%` }} />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Admin Settings - For Platform Admins */}
                        {canReadPlatformAdmin(user) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/admin-settings')}
                                title="Admin Settings"
                            >
                                <Shield size={20} />
                            </Button>
                        )}

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            title="Logout"
                        >
                            <LogOut size={20} />
                        </Button>

                        {/* Debug Toggle */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDebugMode(!debugMode)}
                            title="Debug Mode"
                            className={debugMode ? 'bg-red-100 text-red-600' : ''}
                        >
                            <Bug size={20} />
                        </Button>
                    </div>
                </div>

                {/* Connection Lost State & Broadcast Button Wrapper - Moved out of transcript to be sticky/fixed */}
                {!isConnected && hasEverConnected ? (
                    <div className="empty-state-host">
                        <div
                            className="icon-circle offline clickable"
                            onClick={stopSession}
                            title="Connection lost - Click to reset"
                        >
                            <WifiOff size={48} className="icon-offline" />
                        </div>
                        <h3>Connection Lost</h3>
                        <p className="error-message">
                            {connectionError || 'WebSocket connection was interrupted.'}
                        </p>
                        <p className="error-hint">Click the icon above or Stop button to reset and try again.</p>
                    </div>
                ) : (
                    /* Unified Broadcast Button & Empty State */
                    /* Only show empty state if we are active OR if there are no translations to show */
                    (isSessionActive || (translations.length === 0 && unifiedTranslations.length === 0)) && (
                        <div className={`empty-state-host ${isSessionActive ? 'broadcasting' : ''}`}>
                            <div
                                className={`icon-circle ${isStartingSession ? 'connecting' :
                                    isStoppingSession ? 'connecting' :
                                        isSessionPaused ? 'paused' :
                                            isSessionActive ? 'broadcasting' :
                                                'clickable'
                                    }`}
                                onClick={
                                    isStartingSession ? undefined :
                                        isStoppingSession ? undefined :
                                            isSessionActive ? stopSession :
                                                startSession
                                }
                                title={
                                    isStartingSession ? "Initializing session..." :
                                        isStoppingSession ? "Stopping session..." :
                                            isSessionActive ? "Click to stop session" :
                                                "Click to start session"
                                }
                                style={{ cursor: (isStartingSession || isStoppingSession) ? 'wait' : 'pointer' }}
                            >
                                <Mic
                                    style={{
                                        width: isSessionActive ? '24px' : '24px',
                                        height: isSessionActive ? '24px' : '24px',
                                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                    className={
                                        isStartingSession ? 'mic-connecting' :
                                            isStoppingSession ? 'mic-connecting' :
                                                isSessionPaused ? 'mic-paused' :
                                                    isSessionActive ? 'mic-broadcasting' :
                                                        ''
                                    }
                                />
                            </div>
                            <h3>
                                {isStartingSession ? 'Starting Session...' :
                                    isStoppingSession ? 'Stopping Session...' :
                                        isSessionPaused ? 'Session Paused' :
                                            isSessionActive ? 'Broadcasting Live' :
                                                'Ready to Broadcast'}
                            </h3>
                            <p>
                                {isStartingSession
                                    ? 'Initializing microphone and connections. Please wait...'
                                    : isStoppingSession
                                        ? 'Closing connections and cleaning up resources...'
                                        : isSessionPaused
                                            ? 'Session is paused.'
                                            : isSessionActive
                                                ? 'Listening...'
                                                : 'Click the microphone or Start Session button to begin.'
                                }
                            </p>
                        </div>
                    )
                )}

                {/* Transcript Area */}
                <div className="host-transcript" ref={translationOutputRef}>

                    {/* Transcripts */}
                    {/* Unified Mode Rendering */}
                    {canUnifyModel && unifiedTranslations.map((t, index) => {
                        // Show speaker badge when speaker changes from previous message
                        const prevSpeaker = index > 0 ? unifiedTranslations[index - 1].speaker : null;
                        const showSpeakerBadge = t.speaker && t.speaker !== prevSpeaker;
                        // Cycle through colors based on speaker number (S1, S2, etc.)
                        const speakerNum = t.speaker ? parseInt(t.speaker.replace(/\D/g, '')) || 1 : 1;
                        const speakerColorClass = `speaker-${((speakerNum - 1) % 3) + 1}`;

                        return (
                            <div key={index} className={`host-message ${t.isFinal ? 'final' : 'partial'}`}>
                                {showSpeakerBadge && (
                                    <div className={`speaker-badge ${speakerColorClass}`}>
                                        {t.speaker ? t.speaker.replace(/^(?:S|Speaker\s*)?(\d+)$/i, 'Speaker $1') : t.speaker}
                                    </div>
                                )}
                                {t.isFinal && showSourceText && t.sourceText && (
                                    <div className="source-text-container">
                                        <span className="source-text-label">Original</span>
                                        <div className="source-text">{t.sourceText}</div>
                                    </div>
                                )}
                                <div className="message-content">{renderWithGlossaryHighlights(t.text)}</div>
                                {t.isFinal ? (
                                    <div className="message-meta">{t.timestamp.toLocaleTimeString()}{t.tag && <span className="model-tag"> • {t.tag}</span>}{t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}</div>
                                ) : (
                                    <div className="message-meta">{t.tag && <span className="model-tag">{t.tag}</span>}{t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}</div>
                                )}
                            </div>
                        );
                    })}

                    {/* Traditional Mode Rendering */}
                    {!canUnifyModel && translations.map((t, i) => {
                        // Show speaker badge when speaker changes from previous message
                        const prevSpeaker = i > 0 ? translations[i - 1].speaker : null;
                        const showSpeakerBadge = t.speaker && t.speaker !== prevSpeaker;
                        // Cycle through colors based on speaker number (S1, S2, etc.)
                        const speakerNum = t.speaker ? parseInt(t.speaker.replace(/\D/g, '')) || 1 : 1;
                        const speakerColorClass = `speaker-${((speakerNum - 1) % 3) + 1}`;

                        return (
                            <div key={i} className={`host-message ${t.isFinal ? 'final' : 'partial'}`}>
                                {showSpeakerBadge && (
                                    <div className={`speaker-badge ${speakerColorClass}`}>
                                        {t.speaker ? t.speaker.replace(/^(?:S|Speaker\s*)?(\d+)$/i, 'Speaker $1') : t.speaker}
                                    </div>
                                )}
                                {t.isFinal && showSourceText && t.sourceText && (
                                    <div className="source-text-container">
                                        <span className="source-text-label">Original</span>
                                        <div className="source-text">{t.sourceText}</div>
                                    </div>
                                )}
                                <div className="message-content">{renderWithGlossaryHighlights(t.text)}</div>
                                <div className="message-meta">
                                    {t.isFinal && t.timestamp ? t.timestamp.toLocaleTimeString() : ''}{t.isFinal && t.tag && <span className="model-tag"> • {t.tag}</span>}{t.isFinal && t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}
                                </div>
                            </div>
                        );
                    })}

                    {/* Partial Text (Traditional Mode) */}
                    {!canUnifyModel && (enablePartialText && currentPartial) && (
                        <div className="host-message partial">
                            <div className="message-content">{renderWithGlossaryHighlights(currentPartial)}</div>
                            <div className="message-meta">Processing...</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Quota Exceeded Modal */}
            {/* Upgrade Modal */}
            {
                showUpgradeModal && (
                    <div className="modal-overlay">
                        <div className="quota-exceeded-modal">
                            <div className="modal-icon">
                                <AlertCircle size={48} color="#3B82F6" />
                            </div>
                            <h2>Get More Minutes or Unlimited Access</h2>
                            <p>
                                To request more minutes or unlimited access, please contact us at:
                            </p>
                            <div
                                className="modal-contact-email copyable"
                                onClick={() => {
                                    navigator.clipboard.writeText('support@bignetai.com');
                                    setIsEmailCopied(true);
                                    setTimeout(() => setIsEmailCopied(false), 2000);
                                }}
                                title="Click to copy"
                            >
                                <span>support@bignetai.com</span>
                                {isEmailCopied ? <Check size={18} color="#10B981" /> : <Copy size={18} />}
                            </div>
                            <p className="modal-copy-hint">
                                {isEmailCopied ? 'Copied!' : 'Click to copy'}
                            </p>
                            <Button
                                variant="primary"
                                onClick={() => setShowUpgradeModal(false)}
                            >
                                Got it
                            </Button>
                        </div>
                    </div>
                )
            }

            {/* Debug Console Overlay */}
            {/* Debug Console Overlay */}
            {debugMode && (
                <div className="debug-console" style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '200px',
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    color: '#00ff00',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    padding: '0',
                    overflowY: 'auto',
                    zIndex: 9999,
                    borderTop: '2px solid #333'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px',
                        borderBottom: '1px solid #333',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#111',
                        zIndex: 10
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong>Debug Logs</strong>
                            <span style={{ fontSize: '10px', color: '#888' }}>
                                Trace ID: <span style={{ color: '#00ff00', fontFamily: 'monospace' }}>{getCurrentTraceId() || 'Not started'}</span>
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    const logText = logs.join('\n');
                                    navigator.clipboard.writeText(logText);
                                    // Visual feedback
                                    const btn = document.getElementById('copy-logs-btn');
                                    if (btn) {
                                        const originalText = btn.innerText;
                                        btn.innerText = 'Copied!';
                                        setTimeout(() => btn.innerText = originalText, 2000);
                                    }
                                }}
                                id="copy-logs-btn"
                                style={{ background: 'none', border: '1px solid #555', borderRadius: '4px', padding: '2px 8px', color: '#00ff00', cursor: 'pointer', fontSize: '11px' }}
                            >
                                Copy All
                            </button>
                            <button onClick={() => setLogs([])} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>Clear</button>
                        </div>
                    </div>
                    <div style={{ padding: '10px' }}>
                        {logs.map((log, i) => (
                            <div key={i} style={{ marginBottom: '2px', whiteSpace: 'pre-wrap', borderBottom: '1px solid #333', paddingBottom: '2px', color: '#00ff00' }}>{log}</div>
                        ))}
                        {logs.length === 0 && <div style={{ color: '#888' }}>No logs yet...</div>}
                    </div>
                </div>
            )}
        </div >
    );
};

export default TranslationHostRevised;
